import React, { useState, useEffect, useCallback, useRef, useMemo, lazy, Suspense } from 'react';

import { Icons } from './components/Icons';
import { APIService } from './services/api';
import { AuthStorage, StorageService, ConsentStorage } from './services/storage';
import { Header, Footer } from './components/layout';
import PageLayout from './components/layout/PageLayout';
import SidebarLayout from './components/layouts/SidebarLayout';
import { 
  SearchForm, 
  StickySubNav,
  ComparisonTable,
  RevenueLossCalculator,
  ROICalculator,
  ImpactMonitor
} from './components/sections';
// Result-only heavy sections: lazy-loaded so they stay out of the entry bundle
// (landing / login users never download them). Each render site below has its
// own Suspense boundary.
const CoreVitalsSection = lazy(() => import('./components/sections/CoreVitalsSection'));
const OtherMetricsSection = lazy(() => import('./components/sections/OtherMetricsSection'));
const PlaybookPanel = lazy(() => import('./components/sections/PlaybookPanel'));
const ExecutiveView = lazy(() => import('./components/sections/ExecutiveView'));
const LabResultsSection = lazy(() => import('./components/sections/LabResultsSection'));
const ComparisonPanel = lazy(() => import('./components/sections/ComparisonPanel'));
const SharedComparisonPage = lazy(() => import('./pages/SharedComparisonPage'));
import { ConsentBanner } from './components/ConsentBanner';
import { InteractiveBackground } from './components/InteractiveBackground';
import { useLanguage } from './contexts/LanguageContext';
import useAnimations from './hooks/useAnimations';
import useUserStatus from './hooks/useUserStatus';
import { useModals, useComparisonState, useUserDomains, useAnalysisData, usePageRouting, useViewMode, useSessionId } from './hooks';
import QuotaIndicator from './components/ui/QuotaIndicator';
import RestrictionBanner from './components/ui/RestrictionBanner';
import { LimitExhaustedBanner, GuestWelcomeBanner, RevenueLossSlider, BackedByScience, AnalysisErrorMessage } from './components/ui';
import { detectFailingVitals } from './utils/quickAudit';
import { buildDiagnosticCandidates, detectMetricTrends } from './utils/metricDiagnosticCandidates';
import useDiagnosticHistory from './hooks/useDiagnosticHistory';
import MetricTrendNotices from './components/metrics/MetricTrendNotices';
import AnalysisProgressOverlay from './components/ui/AnalysisProgressOverlay';
const ValueProposition = lazy(() => import('./components/sections/homepage/ValueProposition'));
const HowItWorks = lazy(() => import('./components/sections/homepage/HowItWorks'));
const TargetAudience = lazy(() => import('./components/sections/homepage/TargetAudience'));
const ProductShowcase = lazy(() => import('./components/sections/homepage/ProductShowcase'));
const FAQ = lazy(() => import('./components/sections/homepage/FAQ'));
const PricingPreview = lazy(() => import('./components/sections/homepage/PricingPreview'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const FinalCTA = lazy(() => import('./components/sections/homepage/FinalCTA'));
const IntegrationsLogos = lazy(() => import('./components/sections/homepage/IntegrationsLogos'));
const QuickToolsGrid = lazy(() => import('./components/sections/homepage/QuickToolsGrid'));
const PlaybookPreview = lazy(() => import('./components/sections/homepage/PlaybookPreview'));
const AgencyPreview = lazy(() => import('./components/sections/homepage/AgencyPreview'));
const TopPerformersPreview = lazy(() => import('./components/sections/homepage/TopPerformersPreview'));
const CrossDeviceWeeklyComparison = lazy(() => import('./components/comparison/CrossDeviceWeeklyComparison'));
const SnapshotEvolutionView = lazy(() => import('./components/comparison/SnapshotEvolutionView'));
const DeviceMetricsRadarGrid = lazy(() => import('./components/comparison/DeviceMetricsRadarGrid'));
const BigQueryMetricsSection = lazy(() => import('./components/metrics/BigQueryMetricsSection'));
const FormFactorFractions = lazy(() => import('./components/metrics/FormFactorFractions'));
const LcpImageSubpartsDisplay = lazy(() => import('./components/metrics/LcpImageSubpartsDisplay'));
const ExtendedMetricsDisplay = lazy(() => import('./components/metrics/ExtendedMetricsDisplay'));
const NavigationTypesDisplay = lazy(() => import('./components/metrics/NavigationTypesDisplay'));
const LcpResourceTypeDisplay = lazy(() => import('./components/metrics/LcpResourceTypeDisplay'));
const HistogramDistributionTable = lazy(() => import('./components/tables/HistogramDistributionTable'));
const PageSpeedMetricsSummaryTable = lazy(() => import('./components/tables/PageSpeedMetricsSummaryTable'));
const LighthouseMetricsSummaryTable = lazy(() => import('./components/tables/LighthouseMetricsSummaryTable'));
const DeviceMetricsSummaryTable = lazy(() => import('./components/tables/DeviceMetricsSummaryTable'));
const CrUXHistoryChart = lazy(() => import('./components/charts/CrUXHistoryChart'));
const MonthlyHistorySection = lazy(() => import('./components/charts/MonthlyHistorySection'));
const ScriptMonitoringSection = lazy(() => import('./components/sections/ScriptMonitoringSection'));
import { ResetPasswordPage } from './components/auth';
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const MaintenancePage = lazy(() => import('./pages/MaintenancePage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const DpaPage = lazy(() => import('./pages/DpaPage'));
const CreditsPage = lazy(() => import('./pages/CreditsPage'));
const ClientDashboardPage = lazy(() => import('./pages/ClientDashboardPage'));
const RankingsPage = lazy(() => import('./pages/RankingsPage'));
const TopPerformersPage = lazy(() => import('./pages/TopPerformersPage'));
const HealthCheckPage = lazy(() => import('./pages/HealthCheckPage'));
const QuickAuditPage = lazy(() => import('./pages/QuickAuditPage'));
const HistoryMonitoringPage = lazy(() => import('./pages/HistoryMonitoringPage'));
const CompetitorComparisonPage = lazy(() => import('./pages/CompetitorComparisonPage'));
const MeasurementMethodologyPage = lazy(() => import('./pages/MeasurementMethodologyPage'));
const QuickToolsPage = lazy(() => import('./pages/QuickToolsPage'));
const LabInsightsPage = lazy(() => import('./pages/LabInsightsPage'));
const LabTestPage = lazy(() => import('./pages/LabTestPage'));
const CzechWebsRankingPage = lazy(() => import('./pages/CzechWebsRankingPage'));
const MonitoringPage = lazy(() => import('./pages/MonitoringPage'));
const PlatformStatusPage = lazy(() => import('./pages/PlatformStatusPage'));
const AiCrawlerGuidePage = lazy(() => import('./pages/AiCrawlerGuidePage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const ArticlePage = lazy(() => import('./pages/ArticlePage'));
const EshopHealthCheckPage = lazy(() => import('./pages/EshopHealthCheckPage'));
const MarketInsightsPage = lazy(() => import('./pages/MarketInsightsPage'));
const HomepageVariantB = lazy(() => import('./pages/HomepageVariantB'));
import ErrorBoundary from './components/ErrorBoundary';
import FeedbackWidget from './components/FeedbackWidget';
import { publicUrl } from './config/publicOrigin';
import { AuditCompletionSound, AUDIT_SOUND_KEY, readAuditSoundPreference } from './utils/auditCompletionSound';

const PageSpeedExtendedReport = lazy(() => import('./components/sections/PageSpeedExtendedReport'));
const CategoryScoresDisplay = lazy(() => import('./components/sections/CategoryScoresDisplay'));
const FilmstripDisplay = lazy(() => import('./components/sections/FilmstripDisplay'));
const ResourceSummaryDisplay = lazy(() => import('./components/sections/ResourceSummaryDisplay'));
const MainThreadWorkDisplay = lazy(() => import('./components/sections/MainThreadWorkDisplay'));
const AuditResultsSection = lazy(() => import('./components/sections/AuditResultsSection'));
const StackPacksDisplay = lazy(() => import('./components/sections/StackPacksDisplay'));
const JsLibrariesDisplay = lazy(() => import('./components/sections/JsLibrariesDisplay'));
const FinalScreenshotDisplay = lazy(() => import('./components/sections/FinalScreenshotDisplay'));
const AdditionalAuditsDisplay = lazy(() => import('./components/sections/AdditionalAuditsDisplay'));
const AlertSettings = lazy(() => import('./components/sections/AlertSettings'));
const LabTestPanel = lazy(() => import('./components/sections/LabTestPanel'));
const IndustryBenchmark = lazy(() => import('./components/sections/IndustryBenchmark'));
const BusinessToolsSection = lazy(() => import('./components/sections/BusinessToolsSection'));
const MonitoringGuide = lazy(() => import('./components/sections/MonitoringGuide'));
const BadgesPanel = lazy(() => import('./components/sections/BadgesPanel'));
const AgencyPanel = lazy(() => import('./components/sections/AgencyPanel'));

const FieldVsLabBanner = lazy(() => import('./components/sections/FieldVsLabBanner'));
const QuickAuditButton = lazy(() => import('./components/metrics/QuickAuditModal'));
const MetricDiagnosticModal = lazy(() => import('./components/metrics/QuickAuditModal').then(module => ({ default: module.MetricDiagnosticModal })));
const AdminPanel = lazy(() => import('./components/admin/AdminPanel'));
const AuthModal = lazy(() => import('./components/modals/AuthModal'));
const PricingModal = lazy(() => import('./components/modals/PricingModal'));
const AiNarrativeSettingsModal = lazy(() => import('./components/AiNarrativeSettingsModal'));
const ExportModal = lazy(() => import('./components/modals/ExportModal'));
const AnimationSettingsModal = lazy(() => import('./components/modals/AnimationSettingsModal'));
const AnalysisHistoryModal = lazy(() => import('./components/modals/AnalysisHistoryModal'));
const DomainGroupsModal = lazy(() => import('./components/modals/DomainGroupsModal'));
const MyAnalysesModal = lazy(() => import('./components/modals/MyAnalysesModal'));
const IntegrationGuideModal = lazy(() => import('./components/modals/IntegrationGuideModal'));

const CruxDashboardAlternativa = lazy(() => import('./pages/CruxDashboardAlternativa'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const AuditPage = lazy(() => import('./pages/AuditPage'));
const GlobalInsightsPage = lazy(() => import('./pages/GlobalInsightsPage'));
const LazyFallback = ({ error }) => {
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <div className="text-red-400 mb-2">Nepodařilo se načíst komponentu</div>
        <div className="flex gap-3">
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded text-white text-sm"
          >
            Obnovit stránku
          </button>
          <a
            href={publicUrl('/')}
            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded text-white text-sm"
          >
            ControlVitals
          </a>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-center justify-center py-8">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
    </div>
  );
};

const KNOWN_PATHS = ['/', '/audit', '/web-core-vitals-google-report-crux', '/admin', '/verify-email', '/reset-password', '/terms', '/privacy', '/dpa', '/credits', '/global-insights', '/health-check'];

if (process.env.NODE_ENV !== 'production') {
  const imports = { SearchForm, CoreVitalsSection, OtherMetricsSection, PlaybookPanel, StickySubNav, ComparisonTable, RevenueLossCalculator, ROICalculator, ImpactMonitor, ErrorBoundary };
  Object.entries(imports).forEach(([name, comp]) => {
    if (!comp) console.error(`UNDEFINED COMPONENT: ${name}`);
  });
}

const normalizeHistoryForImpactMonitor = (historyData) => {
  if (!historyData || !Array.isArray(historyData) || historyData.length === 0) return null;
  
  const collectionPeriods = historyData.map(entry => {
    const date = new Date(entry.date);
    return {
      lastDate: {
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        day: date.getDate()
      }
    };
  });
  
  const metrics = {
    LCP: { p75s: historyData.map(e => e.metrics?.LCP?.p75 || null) },
    CLS: { p75s: historyData.map(e => e.metrics?.CLS?.p75 || null) },
    INP: { p75s: historyData.map(e => e.metrics?.INP?.p75 || null) },
    FCP: { p75s: historyData.map(e => e.metrics?.FCP?.p75 || null) },
    TTFB: { p75s: historyData.map(e => e.metrics?.TTFB?.p75 || null) }
  };
  
  return { collectionPeriods, metrics };
};

function App() {
  const comparisonReportToken = new URLSearchParams(window.location.search).get('comparisonReport');
  // Invalidates outstanding requests even when navigation stays on the home route.
  const viewGeneration = useRef(0);
  const [auditSoundEnabled, setAuditSoundEnabled] = useState(readAuditSoundPreference);
  const auditSound = useRef(null);
  if (!auditSound.current) {
    auditSound.current = new AuditCompletionSound();
    auditSound.current.enabled = auditSoundEnabled;
  }
  useEffect(() => {
    // StrictMode replays effects in development. Its cleanup must not leave
    // the next setup using a disposed audio controller.
    if (auditSound.current.disposed) {
      auditSound.current = new AuditCompletionSound();
      auditSound.current.enabled = auditSoundEnabled;
    }
    return () => auditSound.current.dispose();
  }, []);
  const toggleAuditSound = (enabled) => {
    auditSound.current.setEnabled(enabled);
    setAuditSoundEnabled(enabled);
    try { window.localStorage.setItem(AUDIT_SOUND_KEY, String(enabled)); } catch {}
  };
  const { t, language } = useLanguage();
  const {
    currentPage,
    maintenanceMode,
    maintenanceInfo,
    maintenanceChecked,
    navigateTo
  } = usePageRouting();
  const [currentUser, setCurrentUser] = useState(null);
  const [domain, setDomain] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState('ALL');
  const [analysisType, setAnalysisType] = useState('origin');
  const [dataSource, setDataSource] = useState('crux');
  const [searchFormSource, setSearchFormSource] = useState('crux');
  const [labTimeoutError, setLabTimeoutError] = useState(null);
  const [labInitialSubTab, setLabInitialSubTab] = useState(null);
  const [pendingScrollAnchor, setPendingScrollAnchor] = useState(null);
  
  const {
    cruxData, setCruxData,
    lighthouseData, setLighthouseData,
    labData, setLabData,
    historyData, setHistoryData,
    monthlyData, setMonthlyData,
    formFactorFractions, setFormFactorFractions,
    lcpImageSubparts, setLcpImageSubparts,
    navigationTypes, setNavigationTypes,
    lcpResourceType, setLcpResourceType,
    apiStatus, setApiStatus,
    collectionPeriod, setCollectionPeriod,
    currentMetrics, setCurrentMetrics,
    historicalData, setHistoricalData,
    periodsData, setPeriodsData
  } = useAnalysisData();
  
  const {
    showAuthModal, setShowAuthModal,
    showPricingModal, setShowPricingModal,
    showExportModal, setShowExportModal,
    showAdminPanel, setShowAdminPanel,
    showAnimationSettings, setShowAnimationSettings,
    showAnalysisHistory, setShowAnalysisHistory,
    showMyAnalyses, setShowMyAnalyses,
    showAlertSettings, setShowAlertSettings,
    showMonitoringGuide, setShowMonitoringGuide,
    showLabTest, setShowLabTest,
    showDomainGroupsModal, setShowDomainGroupsModal,
    showDataSourceInfo, setShowDataSourceInfo,
    showBadgesPanel, closeBadges, openBadges,
    showAgencyPanel, closeAgency, openAgency,
    showIntegrationGuide, closeIntegrationGuide, openIntegrationGuide,
    authMode, setAuthMode,
    openAlertSettings,
    openMonitoringGuide,
    openLabTest
  } = useModals();
  
  const [activePlaybook, setActivePlaybook] = useState(null);
  const [showStickyNav, setShowStickyNav] = useState(false);
  const [agencyInitialTab, setAgencyInitialTab] = useState(null);
  useEffect(() => {
    function onOpenAgency(e) {
      const tab = e?.detail?.tab;
      if (tab) setAgencyInitialTab(tab);
      openAgency();
    }
    window.addEventListener('openAgency', onOpenAgency);
    return () => window.removeEventListener('openAgency', onOpenAgency);
  }, [openAgency]);

  // Deep-link to open the agency panel on a specific tab. Used by:
  //   - the weekly-digest / CSV digest emails (`/?open=script-monitoring`),
  //     which land directly on the central script monitoring dashboard;
  //   - the weekly-digest unsubscribe redirect, which adds
  //     `&unsubscribed=weekly-digest` so the dashboard can render its
  //     "Odhlášeno" confirmation banner. That banner (and the query-param
  //     cleanup) live inside ScriptMonitoringDashboardTab.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (!params.has('comparisonReport') && params.get('open') === 'script-monitoring') {
        setAgencyInitialTab('script-monitoring');
        openAgency();
      }
    } catch {
      /* non-fatal */
    }
  }, [openAgency]);
  const [savingAnalysis, setSavingAnalysis] = useState(false);
  const [lastSavedAnalysisId, setLastSavedAnalysisId] = useState(null);
  const [showHeaderLogo] = useState(true);
  const [consentShown, setConsentShown] = useState(false);
  const [hiddenPages, setHiddenPages] = useState({});
  
  const {
    compareMode, setCompareMode,
    comparisonData, setComparisonData,
    comparisonMode, setComparisonMode,
    selectedForCompare, setSelectedForCompare,
    availablePeriods, setAvailablePeriods,
    selectedPeriod, setSelectedPeriod,
    loadingPeriods, setLoadingPeriods,
    historyAvailability, setHistoryAvailability,
    syncingHistory, setSyncingHistory,
    batchSyncing, setBatchSyncing,
    syncProgress, setSyncProgress
  } = useComparisonState();
  
  const [resetPasswordToken, setResetPasswordToken] = useState(null);
  const [selectedMetric, setSelectedMetric] = useState('LCP');
  const [siteSettings, setSiteSettings] = useState({
    logo_settings: { showBackground: false, glowIntensity: 0.5, glowColor: '#10b981' },
    global_animations: { logoGlow: true, borderAnimation: true, borderIntensity: 0.85, borderSpeed: 3, borderColor1: '#06b6d4', borderColor2: '#8b5cf6', bgAnimation: false }
  });
  
  const [stickyHeaderVisible, setStickyHeaderVisible] = useState(false);
  const [activeSection, setActiveSection] = useState('prehled');
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [historySelectedMetric, setHistorySelectedMetric] = useState('LCP');
  const [historyExpanded, setHistoryExpanded] = useState(false);
  
  const {
    userDomains, setUserDomains,
    userDomainGroups, setUserDomainGroups,
    userDomainsLimits, setUserDomainsLimits,
    removingDomain, setRemovingDomain,
    trackedOrigins, setTrackedOrigins,
    showCreateGroupForm, setShowCreateGroupForm,
    editingGroup, setEditingGroup,
    newGroupName, setNewGroupName,
    newGroupDescription, setNewGroupDescription,
    newGroupColor, setNewGroupColor,
    bulkImportGroupId, setBulkImportGroupId,
    bulkImportText, setBulkImportText,
    bulkImportLoading, setBulkImportLoading
  } = useUserDomains();
  
  const [emailVerificationStatus, setEmailVerificationStatus] = useState(null);
  
  const [isSharedView, setIsSharedView] = useState(false);
  const [sharedInfo, setSharedInfo] = useState(null);
  const { viewMode, setViewMode } = useViewMode();
  const sessionId = useSessionId();
  const [domains, setDomains] = useState([]);
  const [timeRange, setTimeRange] = useState('28d');
  const [hadPreviousResults, setHadPreviousResults] = useState(false);
  
  const resultsRef = useRef(null);
  const heroSectionRef = useRef(null);
  const progressRef = useRef(null);
  
  const {
    borderAnimationEnabled,
    setBorderAnimationEnabled,
    borderAnimSettings,
    setBorderAnimSettings,
    bgAnimationEnabled,
    setBgAnimationEnabled,
    bgAnimSettings,
    setBgAnimSettings,
    particleAnimationEnabled,
    setParticleAnimationEnabled,
    particleAnimSettings,
    setParticleAnimSettings,
    saveAnimationPreferences,
    resetToDefaults
  } = useAnimations(currentUser, null);
  
  const { status: userStatus, refresh: refreshUserStatus } = useUserStatus({ enabled: currentPage !== 'admin' });
  
  const dataSourceRef = useRef(dataSource);

  useEffect(() => {
    if (loading && progressRef.current) {
      setTimeout(() => {
        progressRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  }, [loading]);

  useEffect(() => {
    const user = AuthStorage.getUser();
    const initialGeneration = viewGeneration.current;
    let hasToken = false;
    try { hasToken = Boolean(sessionStorage.getItem('cv_session_token')); } catch (e) {}
    if (user) setCurrentUser(user);
    // Always hydrate from server when EITHER a cached user OR a session token
    // exists. Newly verified users have no cached user but do have a token —
    // we must still fetch /me so they end up signed-in for the pricing flow.
    if (user || hasToken) {
      APIService.getMe().then(serverUser => {
        if (!serverUser) {
          AuthStorage.removeUser();
          setCurrentUser(null);
        } else if (serverUser.user) {
          AuthStorage.setUser(serverUser.user);
          setCurrentUser(serverUser.user);
        }
      }).catch(() => {
        AuthStorage.removeUser();
        setCurrentUser(null);
      });
    }
    
    const handleSessionExpired = () => {
      setCurrentUser(null);
    };
    window.addEventListener('sessionExpired', handleSessionExpired);
    
    const consent = ConsentStorage.getConsent();
    if (!consent) {
      setConsentShown(true);
    }
    
    setDomains(StorageService.getAllDomains());
    
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    if (token && window.location.pathname === '/reset-password') {
      setResetPasswordToken(token);
    }
    
    const shareId = urlParams.get('share');
    if (comparisonReportToken === null && shareId) {
      loadSharedData(shareId);
    }
    
    const showAnalysesParam = urlParams.get('showAnalyses');
    if (comparisonReportToken === null && showAnalysesParam === 'true') {
      setShowMyAnalyses(true);
      window.history.replaceState({}, '', window.location.pathname);
    }

    // Auto-enable comparison mode when arriving via /?compare=1
    // (e.g. from the "Porovnat s konkurencí" CTA on /competitor-comparison)
    if (comparisonReportToken === null && (urlParams.get('compare') === '1' || window.location.pathname === '/domain-comparison')) {
      setCompareMode(true);
    }

    // Auto-trigger Lab Test when arriving via /?origin=...&tab=lab
    // (e.g. from the "Spustit Lab Test" CTA on /lab-test)
    const labOrigin = urlParams.get('origin');
    if (comparisonReportToken === null && labOrigin && urlParams.get('tab') === 'lab') {
      try {
        const cleaned = labOrigin.replace(/^https?:\/\//, '').replace(/\/$/, '');
        setDomain(cleaned);
        const subTab = urlParams.get('subTab');
        if (subTab) setLabInitialSubTab(subTab);
        // Defer until after currentUser/userStatus hydration so quota checks pass
        setTimeout(() => {
          if (initialGeneration === viewGeneration.current) {
            handleAnalyze(cleaned, 'origin', { source: 'lab', device: 'PHONE' });
          }
        }, 600);
        window.history.replaceState({}, '', '/');
      } catch (e) {
        console.error('Failed to auto-start Lab Test from URL params', e);
      }
    }

    // Auto-trigger CrUX analysis when arriving via /?domain=...[#section-xyz]
    // (e.g. from the agency dashboard "Otevřít detail v analýze webu" link).
    // We honor any URL hash by remembering it and scrolling to it once the
    // analysis data has loaded and the relevant section has mounted.
    // Guard precisely: only skip if the lab deeplink already claimed the URL.
    const queryDomain = urlParams.get('domain');
    const labClaimed = !!(labOrigin && urlParams.get('tab') === 'lab');
    if (comparisonReportToken === null && queryDomain && !labClaimed) {
      try {
        const cleaned = queryDomain.replace(/^https?:\/\//, '').replace(/\/$/, '');
        setDomain(cleaned);
        // Only accept hashes that look like our known section anchors so we
        // can later resolve them safely with getElementById.
        const rawHash = window.location.hash; // e.g. "#section-script-monitoring"
        const safeHash = /^#section-[a-z0-9-]+$/i.test(rawHash) ? rawHash : '';
        if (safeHash) setPendingScrollAnchor(safeHash);
        // Defer until after currentUser/userStatus hydration so quota checks pass
        setTimeout(() => {
          if (initialGeneration === viewGeneration.current) {
            handleAnalyze(cleaned, 'origin', { source: 'crux', device: 'PHONE' });
          }
        }, 600);
        // Strip ?domain= from the URL but keep the hash so a manual refresh
        // still scrolls to the right section once it renders.
        window.history.replaceState({}, '', '/' + (safeHash || ''));
      } catch (e) {
        console.error('Failed to auto-analyze from URL params', e);
      }
    }

    // Auto-open pricing modal after email-verification → "Vybrat tarif" CTA.
    // We delay slightly so the /me hydration above can populate currentUser
    // before PricingModal renders (otherwise the modal pushes the user to
    // the auth screen).
    const openPricingParam = urlParams.get('openPricing');
    if (comparisonReportToken === null && openPricingParam === 'true') {
      setTimeout(() => setShowPricingModal(true), 400);
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, '', cleanUrl);
    }
    
    const checkForEmailVerification = async () => {
      if (window.location.pathname === '/verify-email') {
        const verifyToken = urlParams.get('token');
        if (verifyToken) {
          try {
            const result = await APIService.verifyEmail(verifyToken);
            // Two-step login: backend now returns a one-time loginToken
            // instead of issuing a session on GET. We exchange it via a
            // CSRF-protected POST so a crafted verify link cannot silently
            // sign anyone in.
            let autoLoggedIn = false;
            if (result.loginToken) {
              try {
                const ex = await APIService.exchangeVerifyLogin(result.loginToken);
                if (ex?.token) {
                  try { sessionStorage.setItem('cv_session_token', ex.token); } catch (e) {}
                  autoLoggedIn = true;
                }
              } catch (exErr) {
                console.warn('[verify-email] exchange failed:', exErr);
              }
            }
            setEmailVerificationStatus({
              success: true,
              message: result.message,
              email: result.email,
              autoLoggedIn
            });
          } catch (err) {
            setEmailVerificationStatus({
              success: false,
              message: err.message
            });
          }
        } else {
          setEmailVerificationStatus({
            success: false,
            message: 'Chybí ověřovací token v URL'
          });
        }
      }
    };
    checkForEmailVerification();
    
    const loadSiteSettings = async () => {
      try {
        const res = await fetch('/api/site-settings');
        const data = await res.json();
        if (data.success && data.settings) {
          setSiteSettings(prev => ({
            logo_settings: data.settings.logo_settings || prev.logo_settings,
            global_animations: data.settings.global_animations || prev.global_animations
          }));
          window.siteSettings = data.settings;
        }
      } catch (err) {
        console.log('Using default site settings');
      }
    };
    loadSiteSettings();
    
    const loadHiddenPages = async () => {
      try {
        const res = await fetch('/api/navigation/visibility');
        const data = await res.json();
        setHiddenPages(data.hidden || {});
      } catch (err) {
        console.log('Could not load navigation visibility');
      }
    };
    loadHiddenPages();
    
    const handleSettingsChange = (e) => {
      if (e.detail) {
        setSiteSettings(prev => ({ ...prev, [e.detail.key]: e.detail.value }));
      }
    };
    window.addEventListener('siteSettingsChanged', handleSettingsChange);
    
    const checkApiStatus = async () => {
      try {
        const res = await fetch('/api/status');
        const data = await res.json();
        setApiStatus(data);
      } catch {
        setApiStatus({ status: 'error', apis: { pagespeed: false, crux: false } });
      }
    };
    checkApiStatus();
    
    return () => {
      window.removeEventListener('siteSettingsChanged', handleSettingsChange);
      window.removeEventListener('sessionExpired', handleSessionExpired);
    };
  }, []);

  // When we arrived with /?domain=...#section-xyz the analysis kicks off
  // asynchronously and the target section only mounts once cruxData is in
  // AND its lazy chunk has loaded. Poll for the element until it shows up
  // (or up to 5s), then scroll. Clear pendingScrollAnchor only on success
  // or timeout — never after a single attempt.
  useEffect(() => {
    if (!pendingScrollAnchor || !cruxData) return;
    const id = pendingScrollAnchor.replace(/^#/, '');
    let cancelled = false;
    const startedAt = Date.now();
    const TIMEOUT_MS = 5000;
    const tryScroll = () => {
      if (cancelled) return;
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setPendingScrollAnchor(null);
        return;
      }
      if (Date.now() - startedAt >= TIMEOUT_MS) {
        setPendingScrollAnchor(null);
        return;
      }
      requestAnimationFrame(tryScroll);
    };
    // Give the first paint a moment, then start polling
    const t = setTimeout(tryScroll, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [pendingScrollAnchor, cruxData]);

  const [alertSettingsDefaultTab, setAlertSettingsDefaultTab] = useState(null);
  const [showAiNarrativeSettings, setShowAiNarrativeSettings] = useState(false);
  
  useEffect(() => {
    const handleOpenAdmin = () => {
      if (currentUser?.is_admin) setShowAdminPanel(true);
    };
    const handleOpenAnimationSettings = () => setShowAnimationSettings(true);
    const handleOpenAnalysisHistory = () => setShowAnalysisHistory(true);
    const handleOpenMyAnalyses = () => setShowMyAnalyses(true);
    const handleOpenAlertSettings = (e) => {
      const tab = e.detail?.tab || null;
      setAlertSettingsDefaultTab(tab);
      setShowAlertSettings(true);
    };
    const handleOpenAiNarrativeSettings = () => setShowAiNarrativeSettings(true);
    
    window.addEventListener('openAdminPanel', handleOpenAdmin);
    window.addEventListener('openAnimationSettings', handleOpenAnimationSettings);
    window.addEventListener('openAnalysisHistory', handleOpenAnalysisHistory);
    window.addEventListener('openMyAnalyses', handleOpenMyAnalyses);
    window.addEventListener('openAlertSettings', handleOpenAlertSettings);
    window.addEventListener('openAiNarrativeSettings', handleOpenAiNarrativeSettings);
    
    return () => {
      window.removeEventListener('openAdminPanel', handleOpenAdmin);
      window.removeEventListener('openAnimationSettings', handleOpenAnimationSettings);
      window.removeEventListener('openAnalysisHistory', handleOpenAnalysisHistory);
      window.removeEventListener('openMyAnalyses', handleOpenMyAnalyses);
      window.removeEventListener('openAlertSettings', handleOpenAlertSettings);
      window.removeEventListener('openAiNarrativeSettings', handleOpenAiNarrativeSettings);
    };
  }, [currentUser?.is_admin]);

  useEffect(() => {
    const handleScroll = () => {
      // Show sticky nav earlier - when user scrolls past 200px and has data
      const scrollY = window.scrollY || document.documentElement.scrollTop;
      setShowStickyNav(scrollY > 200 && (cruxData || currentMetrics));
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [cruxData, currentMetrics]);


  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const loadUserDomains = async () => {
      if (!currentUser) {
        setUserDomains([]);
        setUserDomainGroups([]);
        setUserDomainsLimits(null);
        return;
      }
      
      try {
        const res = await fetch('/api/user/domains', { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setUserDomains(data.domains || []);
          setUserDomainGroups(data.groups || []);
          setUserDomainsLimits(data.limits || null);
        }
      } catch (e) {
        console.warn('Failed to load user domains:', e);
      }
    };
    
    loadUserDomains();
    
    const handleOpenDomainGroups = () => setShowDomainGroupsModal(true);
    window.addEventListener('openDomainGroups', handleOpenDomainGroups);
    
    return () => {
      window.removeEventListener('openDomainGroups', handleOpenDomainGroups);
    };
  }, [currentUser]);

  useEffect(() => {
    const loadTrackedOrigins = async () => {
      if (!currentUser) {
        setTrackedOrigins([]);
        return;
      }
      
      try {
        const res = await fetch('/api/user/tracked-origins', { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setTrackedOrigins(data.origins || []);
        }
      } catch (e) {
        console.warn('Failed to load tracked origins:', e);
      }
    };
    
    loadTrackedOrigins();
  }, [currentUser]);

  useEffect(() => {
    if (comparisonMode === 'historical' && selectedForCompare.length >= 2) {
      loadAvailablePeriods(selectedForCompare);
    }
  }, [comparisonMode, JSON.stringify(selectedForCompare)]);

  useEffect(() => {
    if (compareMode && comparisonMode === 'current') {
      setComparisonData(null);
    }
  }, [compareMode, comparisonMode, JSON.stringify(selectedForCompare)]);

  const normalizeDeviceForSource = (source, device) => {
    const isPageSpeedMode = source === 'pagespeed' || source === 'lighthouse';
    if (isPageSpeedMode && (device === 'ALL' || device === 'TABLET' || device === 'COMPARE')) {
      return 'BOTH';
    }
    if (!isPageSpeedMode && device === 'BOTH') {
      return 'ALL';
    }
    return device;
  };
  
  const handleDataSourceChange = (newSource) => {
    dataSourceRef.current = newSource;
    setDataSource(newSource);
    
    const normalizedDevice = normalizeDeviceForSource(newSource, selectedDevice);
    if (normalizedDevice !== selectedDevice) {
      setSelectedDevice(normalizedDevice);
    }
  };
  
  useEffect(() => {
    const isPageSpeedMode = dataSource === 'pagespeed' || dataSource === 'lighthouse';
    if (isPageSpeedMode && (selectedDevice === 'ALL' || selectedDevice === 'TABLET' || selectedDevice === 'COMPARE')) {
      setSelectedDevice('BOTH');
    }
    if (!isPageSpeedMode && selectedDevice === 'BOTH') {
      setSelectedDevice('ALL');
    }
  }, [dataSource]);

  const handleFormFactorChange = async (newFormFactor) => {
    const normalizedDevice = normalizeDeviceForSource(dataSource, newFormFactor);
    setSelectedDevice(normalizedDevice);
    setError(null);
    
    if (normalizedDevice === 'COMPARE') {
      if (cruxData && cruxData.byFormFactor) {
        const hasAnyData = Object.values(cruxData.byFormFactor).some(d => d.metrics && !d.error);
        if (!hasAnyData) {
          setError('Pro tuto doménu nejsou k dispozici data pro porovnání zařízení.');
        }
      }
      return;
    }
    
    if (compareMode && comparisonMode === 'historical' && selectedPeriod && Array.isArray(comparisonData)) {
      const generation = ++viewGeneration.current;
      auditSound.current.cancel();
      setLoading(true);
      try {
        const results = await APIService.getHistoricalComparison(selectedForCompare, selectedPeriod, normalizedDevice);
        if (generation !== viewGeneration.current) return;
        setComparisonData(results);
      } catch (err) {
        if (generation !== viewGeneration.current) return;
        setComparisonData([]);
        setError(err.message);
      } finally {
        if (generation === viewGeneration.current) setLoading(false);
      }
      return;
    }
    
    if (compareMode && comparisonMode === 'current' && Array.isArray(comparisonData)) {
      const generation = ++viewGeneration.current;
      auditSound.current.cancel();
      setLoading(true);
      try {
        const results = await APIService.compareDomains(selectedForCompare, normalizedDevice);
        if (generation !== viewGeneration.current) return;
        setComparisonData(results);
      } catch (err) {
        if (generation !== viewGeneration.current) return;
        setComparisonData([]);
        setError(err.message);
      } finally {
        if (generation === viewGeneration.current) setLoading(false);
      }
      return;
    }
    
    const ffLabel = normalizedDevice === 'PHONE' ? 'Mobil' : 
                   normalizedDevice === 'DESKTOP' ? 'Desktop' : 
                   normalizedDevice === 'TABLET' ? 'Tablet' : 'Všechna zařízení';
    
    const isPageSpeedMode = dataSource === 'pagespeed' || dataSource === 'lighthouse';
    
    if (isPageSpeedMode && lighthouseData?.byFormFactor) {
      // For PageSpeed mode, coerce BOTH to PHONE for display
      const displayDevice = normalizedDevice === 'BOTH' ? 'PHONE' : normalizedDevice;
      const data = lighthouseData.byFormFactor[displayDevice];
      
      if (data?.metrics) {
        console.log(`[PageSpeed] Switching to ${displayDevice}, metrics:`, Object.keys(data.metrics));
        setCurrentMetrics(data.metrics);
        // Update selectedDevice to the actual display device if it was BOTH
        if (normalizedDevice === 'BOTH' && displayDevice !== normalizedDevice) {
          setSelectedDevice(displayDevice);
        }
      } else {
        console.warn(`[PageSpeed] No metrics found for ${displayDevice}`);
        // Try fallback to other device
        const fallbackDevice = displayDevice === 'PHONE' ? 'DESKTOP' : 'PHONE';
        const fallbackData = lighthouseData.byFormFactor[fallbackDevice];
        if (fallbackData?.metrics) {
          console.log(`[PageSpeed] Falling back to ${fallbackDevice}`);
          setCurrentMetrics(fallbackData.metrics);
          setSelectedDevice(fallbackDevice);
        }
      }
      return;
    }
    
    if (cruxData && cruxData.byFormFactor?.[normalizedDevice]) {
      const data = cruxData.byFormFactor[normalizedDevice];
      if (data.error) {
        setError('Data pro "' + ffLabel + '" nejsou k dispozici. Zkuste jiný typ zařízení.');
      } else if (data.metrics) {
        setCurrentMetrics(data.metrics);
      } else {
        setError('Data pro "' + ffLabel + '" nejsou k dispozici. Zkuste jiný typ zařízení.');
      }
      if (data.navigationTypes) {
        setNavigationTypes(data.navigationTypes);
      }
      if (data.lcpResourceType) {
        setLcpResourceType(data.lcpResourceType);
      }
    } else if (cruxData && normalizedDevice !== 'ALL') {
      setError('Data pro "' + ffLabel + '" nejsou k dispozici. Zkuste jiný typ zařízení.');
    }
  };

  const handleRemoveUserDomain = async (originId) => {
    if (!currentUser) return;
    
    setRemovingDomain(originId);
    try {
      const csrfRes = await fetch('/api/csrf-token');
      const { token: csrfToken } = await csrfRes.json();
      
      const res = await fetch(`/api/user/domains/${originId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'X-CSRF-Token': csrfToken }
      });
      
      if (res.ok) {
        setUserDomains(prev => prev.filter(d => d.id !== originId));
      }
    } catch (e) {
      console.error('Failed to remove domain:', e);
    } finally {
      setRemovingDomain(null);
    }
  };

  const handleAnalyze = async (inputDomain, type = 'origin', options = {}) => {
    const targetDomain = inputDomain || domain;
    if (!targetDomain.trim()) return;
    
    const { source = 'crux', device = 'ALL' } = options;
    const normalizedDevice = normalizeDeviceForSource(source, device);
    
    const isSourceLimitExhausted = (src) => {
      if (!userStatus?.quotas) return false;
      if (src === 'crux' && userStatus.quotas.crux?.percentage >= 100) return true;
      if ((src === 'pagespeed' || src === 'lab') && userStatus.quotas.pagespeed?.percentage >= 100) return true;
      return false;
    };
    
    if (isSourceLimitExhausted(source)) {
      const banner = document.getElementById('limit-exhausted-banner');
      if (banner) {
        banner.scrollIntoView({ behavior: 'smooth', block: 'center' });
        banner.classList.add('ring-2', 'ring-amber-400', 'ring-offset-2', 'ring-offset-slate-900');
        setTimeout(() => {
          banner.classList.remove('ring-2', 'ring-amber-400', 'ring-offset-2', 'ring-offset-slate-900');
        }, 2000);
      }
      return;
    }
    // Only a fresh submission from the audit form may chime; URL auto-runs and
    // opening saved results do not enter this path with userInitiated set.
    const soundToken = auditSound.current.start(options.userInitiated === true);
    const generation = ++viewGeneration.current;
    
    setDomain(targetDomain);
    setAnalysisType(type);
    setSelectedDevice(normalizedDevice);
    setDataSource(source);
    setLoading(true);
    setError(null);
    setCruxData(null);
    setLighthouseData(null);
    setLabData(null);
    setComparisonData(null);
    setHistoryData(null);
    setMonthlyData(null);
    setActivePlaybook(null);
    setFormFactorFractions(null);
    setLcpImageSubparts(null);
    setNavigationTypes(null);
    setLcpResourceType(null);
    setCollectionPeriod(null);
    setCurrentMetrics(null);
    setHistoricalData(null);
    
    try {
      const cleanDomain = targetDomain.replace(/^https?:\/\//, '').replace(/\/+$/, '');
      const origin = type === 'url' ? targetDomain : `https://${cleanDomain}`;
      
      let cruxResult = null;
      let lighthouseResult = null;
      
      if (source === 'crux' || source === 'both') {
        try {
          cruxResult = await APIService.getCrUXComprehensive(origin, type, normalizedDevice);
          if (generation !== viewGeneration.current) return;
          setCruxData(cruxResult);
          if (cruxResult?.formFactorFractions) {
            setFormFactorFractions(cruxResult.formFactorFractions);
          }
          if (cruxResult?.lcpImageSubparts) {
            setLcpImageSubparts(cruxResult.lcpImageSubparts);
          }
          if (cruxResult?.byFormFactor?.ALL?.navigationTypes) {
            setNavigationTypes(cruxResult.byFormFactor.ALL.navigationTypes);
          }
          if (cruxResult?.byFormFactor?.ALL?.lcpResourceType) {
            setLcpResourceType(cruxResult.byFormFactor.ALL.lcpResourceType);
          }
          if (cruxResult?.collectionPeriod) {
            setCollectionPeriod(cruxResult.collectionPeriod);
          }
        } catch (e) {
          console.warn('CrUX fetch failed:', e);
          if (source === 'crux') throw e;
        }
      }
      
      if (source === 'lighthouse' || source === 'pagespeed' || source === 'both') {
        try {
          if (normalizedDevice === 'BOTH') {
            const [mobileResult, desktopResult] = await Promise.all([
              APIService.analyzeURL(origin, 'mobile', type),
              APIService.analyzeURL(origin, 'desktop', type)
            ]);
            if (generation !== viewGeneration.current) return;
            lighthouseResult = {
              byFormFactor: {
                PHONE: { metrics: mobileResult?.metrics, ...mobileResult },
                DESKTOP: { metrics: desktopResult?.metrics, ...desktopResult }
              },
              metrics: mobileResult?.metrics,
              mobile: mobileResult,
              desktop: desktopResult
            };
            setSelectedDevice('PHONE');
          } else {
            const psDevice = normalizedDevice === 'DESKTOP' ? 'desktop' : 'mobile';
            const psResult = await APIService.analyzeURL(origin, psDevice, type);
            if (generation !== viewGeneration.current) return;
            lighthouseResult = {
              byFormFactor: {
                [normalizedDevice]: { metrics: psResult?.metrics, ...psResult }
              },
              metrics: psResult?.metrics,
              [psDevice]: psResult
            };
          }
          setLighthouseData(lighthouseResult);
        } catch (e) {
          console.warn('Lighthouse fetch failed:', e);
          if (source === 'lighthouse' || source === 'pagespeed') throw e;
        }
      }
      
      let labResult = null;
      
      if (source === 'lab') {
        try {
          const csrfRes = await fetch('/api/csrf-token', { credentials: 'include' });
          const { token: csrfToken } = await csrfRes.json();
          if (generation !== viewGeneration.current) return;
          
          const labDevice = (device === 'desktop' || device === 'DESKTOP') ? 'desktop' : 'mobile';
          const testUrl = origin;
          const labThrottling = options.throttling || 'fast3g';
          const labCmpMode = options.cmpMode || 'default';
          const labModules = options.modules || ['basic', 'lighthouse', 'accessibility', 'thirdParty', 'coverage'];
          
          const res = await fetch('/api/lab-test', {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json',
              'X-CSRF-Token': csrfToken
            },
            credentials: 'include',
            body: JSON.stringify({ 
              url: testUrl, 
              device: labDevice, 
              throttling: labThrottling,
              modules: labModules,
              cmpMode: labCmpMode
            })
          });
          
          const contentType = res.headers.get('content-type');
          if (!contentType || !contentType.includes('application/json')) {
            throw new Error('Server neodpověděl správně. Zkuste to prosím znovu.');
          }
          
          const data = await res.json();
          if (generation !== viewGeneration.current) return;
          
          if (!res.ok) {
            // Check for timeout error with helpful details
            if (data.errorType === 'timeout' && data.details) {
              setLabTimeoutError({
                message: data.message,
                details: data.details,
                url: normalizedInput
              });
              throw new Error(data.error || 'Lab test selhal');
            }
            throw new Error(data.error || 'Lab test selhal');
          }
          
          setLabTimeoutError(null);
          labResult = data.result || data;
          setLabData(labResult);
          
          if (labResult.metrics) {
            setCurrentMetrics({
              LCP: { value: labResult.metrics.lcp, p75: labResult.metrics.lcp },
              FCP: { value: labResult.metrics.fcp, p75: labResult.metrics.fcp },
              CLS: { value: labResult.metrics.cls, p75: labResult.metrics.cls },
              TTFB: { value: labResult.metrics.ttfb, p75: labResult.metrics.ttfb }
            });
          }
        } catch (e) {
          console.warn('Lab test failed:', e);
          throw e;
        }
      }
      
      if (!cruxResult && !lighthouseResult && !labResult) {
        throw new Error('Nepodařilo se získat žádná data');
      }
      if (generation !== viewGeneration.current) return;
      auditSound.current.complete(soundToken);
      
      // Auto-select device with priority: PHONE → DESKTOP → ALL
      // When device is 'ALL' (default), apply preference for PHONE if available
      // When device is explicit (PHONE/DESKTOP), respect user choice if data available
      let effectiveDevice = normalizedDevice;
      
      // For PageSpeed BOTH mode, default to PHONE
      const isPageSpeedMode = source === 'pagespeed' || source === 'lighthouse';
      if (isPageSpeedMode && normalizedDevice === 'BOTH') {
        effectiveDevice = 'PHONE';
        setSelectedDevice('PHONE');
      } else if (cruxResult?.byFormFactor) {
        const hasPhone = cruxResult.byFormFactor.PHONE?.metrics && !cruxResult.byFormFactor.PHONE?.error;
        const hasDesktop = cruxResult.byFormFactor.DESKTOP?.metrics && !cruxResult.byFormFactor.DESKTOP?.error;
        const hasAll = cruxResult.byFormFactor.ALL?.metrics && !cruxResult.byFormFactor.ALL?.error;
        const hasRequestedDevice = cruxResult.byFormFactor[normalizedDevice]?.metrics && !cruxResult.byFormFactor[normalizedDevice]?.error;
        
        // For CrUX mode: ALWAYS respect user's device selection
        // No automatic switching - user chose this device explicitly
        // Only fallback if the explicitly chosen device has NO data at all
        if (normalizedDevice === 'ALL') {
          // User wants ALL - keep it. ALL data is always available in CrUX as aggregated data
          effectiveDevice = 'ALL';
        } else if (!hasRequestedDevice) {
          // User's explicit choice (PHONE/DESKTOP/TABLET) has no data - fallback
          if (normalizedDevice === 'PHONE' && !hasPhone) {
            effectiveDevice = hasAll ? 'ALL' : (hasDesktop ? 'DESKTOP' : 'ALL');
          } else if (normalizedDevice === 'DESKTOP' && !hasDesktop) {
            effectiveDevice = hasAll ? 'ALL' : (hasPhone ? 'PHONE' : 'ALL');
          }
          if (effectiveDevice !== normalizedDevice) {
            setSelectedDevice(effectiveDevice);
          }
        }
        // else: User's explicit choice has data - keep it, don't change anything
      }
      
      // Set currentMetrics based on effective device or data source
      if (isPageSpeedMode && lighthouseResult?.byFormFactor?.[effectiveDevice]?.metrics) {
        setCurrentMetrics(lighthouseResult.byFormFactor[effectiveDevice].metrics);
      } else if (cruxResult?.byFormFactor?.[effectiveDevice]?.metrics) {
        setCurrentMetrics(cruxResult.byFormFactor[effectiveDevice].metrics);
      } else if (cruxResult?.byFormFactor?.ALL?.metrics) {
        setCurrentMetrics(cruxResult.byFormFactor.ALL.metrics);
      } else if (lighthouseResult?.metrics) {
        // For pagespeed mode, use lighthouse metrics
        setCurrentMetrics(lighthouseResult.metrics);
      }
      
      // Save minimal data to avoid localStorage quota issues
      try {
        StorageService.saveMetrics(cleanDomain, {
          type,
          device: effectiveDevice,
          source,
          timestamp: Date.now()
        });
      } catch (e) {
        console.warn('Failed to save metrics to storage:', e);
      }
      
      setDomains(StorageService.getAllDomains());
      
      trackEvent('analysis_complete', { origin: cleanDomain, source, device: effectiveDevice }, effectiveDevice);
      
      // Emit event for useUserStatus hook to refresh quotas
      window.dispatchEvent(new CustomEvent('analysisComplete'));
      
      // Mark that we've had results - keeps compact hero during subsequent analyses
      setHadPreviousResults(true);
      
      const auditSessionId = cruxResult?.auditSessionId || null;
      
      // Fetch history only if not in URL mode or if specifically requested
      if (type !== 'url') {
        try {
          const history = await APIService.getHistory(cleanDomain, 90);
          if (generation !== viewGeneration.current) return;
          setHistoryData(history);
        } catch (e) {
          console.warn('History fetch failed:', e);
        }
      } else {
        setHistoryData(null);
      }
      
      try {
        const monthly = await APIService.getBigQueryMonthly(origin, auditSessionId);
        if (generation !== viewGeneration.current) return;
        setMonthlyData(monthly);
      } catch (e) {
        console.warn('Monthly data fetch failed:', e);
      }
      
    } catch (err) {
      auditSound.current.cancel(soundToken);
      if (generation !== viewGeneration.current) return;
      setError(err.message || 'Analýza selhala');
    } finally {
      if (generation === viewGeneration.current) setLoading(false);
    }
  };

  const handleCompare = async (domains, options = {}) => {
    if (!domains || domains.length < 2) return;
    const generation = ++viewGeneration.current;
    const soundToken = auditSound.current.start(options.userInitiated === true);
    
    const { source = 'crux', device = 'ALL' } = options;
    const normalizedDevice = normalizeDeviceForSource(source, device);
    
    setDataSource(source);
    setSelectedDevice(normalizedDevice);
    setLoading(true);
    setError(null);
    setCruxData(null);
    setLighthouseData(null);
    setComparisonData(null);
    setActivePlaybook(null);
    
    try {
      const results = await APIService.compareDomains(domains, normalizedDevice, { force: true });
      if (generation !== viewGeneration.current) return;
      setComparisonData(results);
      if (Array.isArray(results) && results.some(result => result.success === true || result.data?.success === true)) {
        auditSound.current.complete(soundToken);
      }
    } catch (err) {
      auditSound.current.cancel(soundToken);
      if (generation !== viewGeneration.current) return;
      setError(err.message || 'Porovnání selhalo');
    } finally {
      if (generation === viewGeneration.current) setLoading(false);
    }
  };

  const enterCompareMode = () => {
    if (domain) {
      setSelectedForCompare(prev => prev.length ? prev : [domain.replace(/^https?:\/\//, '').replace(/\/.*$/, '')]);
    }
    setCompareMode(true);
  };

  // Enter comparison without requiring a previous single-domain result.
  // Comparison results belong to that view; entering the homepage from any
  // other page must not leave the comparison form/results covering the audit.
  const previousPageForComparison = useRef(currentPage);
  useEffect(() => {
    if (currentPage === 'domain-comparison') setCompareMode(true);
    else if (previousPageForComparison.current !== 'home' &&
      currentPage === 'home' && new URLSearchParams(window.location.search).get('compare') !== '1') {
      setCompareMode(false);
      setComparisonData(null);
    }
    previousPageForComparison.current = currentPage;
  }, [currentPage]);

  const handleLogin = (user) => {
    if (user) {
      setCurrentUser(user);
      setShowAuthModal(false);
      trackEvent('user_login', { userId: user.id });
    }
  };

  const handleRegister = async (email, password) => {
    try {
      const result = await APIService.register(email, password);
      return result;
    } catch (err) {
      return { success: false, message: err.message };
    }
  };

  const handleLogout = async () => {
    try {
      await APIService.logout();
    } catch (e) {
      console.warn('Logout API failed:', e);
    }
    AuthStorage.removeUser();
    setCurrentUser(null);
  };

  const handleLoadLabHistoryTest = async (testId) => {
    try {
      const res = await fetch(`/api/lab-test/history/${testId}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Test nenalezen');
      const data = await res.json();
      if (data.result_data) {
        const labResult = { ...data.result_data, id: testId };
        setLabData(labResult);
        
        if (data.url) {
          try {
            const urlObj = new URL(data.url);
            setDomain(urlObj.origin);
          } catch {}
        }
        
        if (labResult.metrics) {
          setCurrentMetrics({
            LCP: { value: labResult.metrics.lcp, p75: labResult.metrics.lcp },
            FCP: { value: labResult.metrics.fcp, p75: labResult.metrics.fcp },
            CLS: { value: labResult.metrics.cls, p75: labResult.metrics.cls },
            TTFB: { value: labResult.metrics.ttfb, p75: labResult.metrics.ttfb }
          });
        }
      }
    } catch (e) {
      console.error('Failed to load Lab test:', e);
    }
  };

  const handleSaveLabTest = async (labResult) => {
    if (!labResult?.url) throw new Error('No URL');
    
    const metricsData = labResult.metrics || {};
    const analysisData = {
      url: labResult.url,
      device: labResult.device || 'mobile',
      metrics: {
        LCP: { p75: metricsData.lcp },
        CLS: { p75: metricsData.cls },
        INP: { p75: metricsData.inp },
        TTFB: { p75: metricsData.ttfb },
        FCP: { p75: metricsData.fcp },
      },
      lighthouse: labResult.lighthouse,
      healthScore: labResult.healthScore,
    };

    return APIService.saveAnalysis({
        url: labResult.url,
        title: `Lab Test: ${labResult.url}`,
        analysisData,
        diagnosticsData: labResult,
        device: labResult.device || 'mobile',
        dataSource: 'lab-test'
    });
  };

  const handleSaveCruxAnalysis = async () => {
    if (savingAnalysis || (!cruxData && !lighthouseData)) return;
    setSavingAnalysis(true);
    try {
      
      const targetUrl = cruxData?.usedTarget || domain || '';
      const analysisData = {
        metrics: currentMetrics || {},
        byFormFactor: cruxData?.byFormFactor || null,
        usedTarget: cruxData?.usedTarget || null,
        collectionPeriod: cruxData?.collectionPeriod || null,
        formFactorFractions: cruxData?.formFactorFractions || null,
        navigationTypes: cruxData?.navigationTypes || null,
      };

      const data = await APIService.saveAnalysis({
          url: targetUrl,
          title: `CrUX: ${targetUrl}`,
          analysisData,
          diagnosticsData: lighthouseData || null,
          device: selectedDevice || 'ALL',
          dataSource: dataSource || 'crux'
      });
      setLastSavedAnalysisId(data.id);
      setTimeout(() => setLastSavedAnalysisId(null), 3000);
    } catch (e) {
      console.error('Failed to save CrUX analysis:', e);
    } finally {
      setSavingAnalysis(false);
    }
  };

  const handleExport = async (format) => {
    if (!cruxData || !domain) return;
    
    try {
      const cleanDomain = domain.replace(/^https?:\/\//, '').replace(/\/+$/, '');
      
      if (format === 'xlsx' || format === 'csv') {
        const blob = await APIService.exportData(cleanDomain, format, {
          crux: cruxData,
          lighthouse: lighthouseData,
          history: historyData,
          monthly: monthlyData
        });
        
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${cleanDomain}_report.${format}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } else if (format === 'pdf') {
        const blob = await APIService.exportPDF(cleanDomain, {
          crux: cruxData,
          lighthouse: lighthouseData,
          history: historyData,
          monthly: monthlyData
        });
        
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${cleanDomain}_report.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
      
      setShowExportModal(false);
    } catch (err) {
      console.error('Export failed:', err);
      alert('Export selhal: ' + err.message);
    }
  };

  const handleConsent = (consent) => {
    ConsentStorage.setConsent(consent);
    setConsentShown(false);
    
    if (typeof gtag === 'function') {
      gtag('consent', 'update', {
        'analytics_storage': consent.analytics ? 'granted' : 'denied',
        'ad_storage': consent.marketing ? 'granted' : 'denied',
        'personalization_storage': consent.marketing ? 'granted' : 'denied'
      });
    }
  };

  const trackEvent = async (eventType, eventData = {}, formFactor = null) => {
    try {
      await fetch('/api/analytics/track', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          sessionId,
          eventType,
          eventData,
          originAnalyzed: domain,
          formFactor: formFactor || selectedDevice
        })
      });
    } catch (e) {
      console.error('Analytics tracking failed:', e);
    }
  };

  const displayDomain = (origin) => {
    if (!origin) return '';
    return origin.replace(/^https?:\/\//, '').replace(/\/$/, '');
  };

  const handleDeleteDomain = (domainToDelete) => {
    const stored = StorageService.getAllDomains();
    const updated = stored.filter(d => d !== domainToDelete);
    localStorage.setItem('cv-domains', JSON.stringify(updated));
    setDomains(updated);
  };

  const loadDomainHistory = async (originToLoad) => {
    try {
      const normalizedDevice = normalizeDeviceForSource(dataSource, selectedDevice);
      const result = await APIService.getCrUXHistory(originToLoad, normalizedDevice);
      if (result && result.history) {
        setHistoricalData(result);
      }
    } catch (e) {
      console.warn('Failed to load domain history:', e);
    }
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerHeight = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerHeight;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setActiveSection(sectionId);
    }
  };

  const checkAPIHealth = async () => {
    try {
      const res = await fetch('/api/status');
      const data = await res.json();
      setApiStatus(data);
    } catch {
      setApiStatus({ status: 'error', apis: { pagespeed: false, crux: false } });
    }
  };

  const loadSharedData = async (shareId) => {
    const generation = viewGeneration.current;
    try {
      const response = await fetch(`/api/share/${shareId}`);
      if (!response.ok) throw new Error('Sdílená data nenalezena');
      
      const sharedData = await response.json();
      if (generation !== viewGeneration.current) return;
      
      setIsSharedView(true);
      setSharedInfo({
        shareId,
        createdAt: sharedData.createdAt,
        expiresAt: sharedData.expiresAt,
        origin: sharedData.origin
      });
      
      if (sharedData.origin) {
        const cleanDomain = sharedData.origin.replace(/^https?:\/\//, '');
        setDomain(cleanDomain);
      }
      
      if (sharedData.cruxData) {
        setCruxData(sharedData.cruxData);
        setCurrentMetrics(sharedData.cruxData.metrics || null);
        if (sharedData.cruxData.formFactorFractions) {
          setFormFactorFractions(sharedData.cruxData.formFactorFractions);
        }
        if (sharedData.cruxData.lcpImageSubparts) {
          setLcpImageSubparts(sharedData.cruxData.lcpImageSubparts);
        }
      }
      
      if (sharedData.lighthouseData) {
        setLighthouseData(sharedData.lighthouseData);
      }
      
      const sharedDataSource = sharedData.dataSource || 'crux';
      if (sharedData.formFactor) {
        const normalizedDevice = normalizeDeviceForSource(sharedDataSource, sharedData.formFactor);
        setSelectedDevice(normalizedDevice);
      }
      
      setDataSource(sharedDataSource);
    } catch (err) {
      if (generation !== viewGeneration.current) return;
      console.error('Failed to load shared data:', err);
      setError('Nepodařilo se načíst sdílená data');
      setIsSharedView(false);
      setSharedInfo(null);
    }
  };

  const getCanonicalDomain = (domain) => {
    if (!domain || typeof domain !== 'string') return '';
    let cleaned = domain.trim().toLowerCase();
    cleaned = cleaned.replace(/^https?:\/\//, '');
    cleaned = cleaned.replace(/\/.*$/, '');
    cleaned = cleaned.replace(/^www\./, '');
    return cleaned;
  };

  const normalizeUrl = (input, keepPath = false) => {
    if (!input) return '';
    let url = input.trim().toLowerCase();
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'https://' + url;
    }
    try {
      const parsed = new URL(url);
      let normalized = parsed.origin;
      if (keepPath && parsed.pathname !== '/') {
        normalized += parsed.pathname.replace(/\/$/, '');
      }
      return normalized;
    } catch {
      return input;
    }
  };

  const isDomainSelected = (domain, selected) => {
    return selected?.some(d => getCanonicalDomain(d) === getCanonicalDomain(domain));
  };

  const addDomainToCompare = () => {
    if (!domain) return;
    const normalized = normalizeUrl(domain);
    if (normalized && !isDomainSelected(normalized, selectedForCompare)) {
      setSelectedForCompare(prev => [...prev, normalized]);
      setDomain('');
      setAvailablePeriods([]);
      setSelectedPeriod(null);
    }
  };

  const toggleDomainForCompare = (origin) => {
    const canonical = getCanonicalDomain(origin);
    setSelectedForCompare(prev => {
      const existingIdx = prev.findIndex(o => getCanonicalDomain(o) === canonical);
      if (existingIdx !== -1) {
        return prev.filter((_, idx) => idx !== existingIdx);
      }
      return [...prev, origin];
    });
    setAvailablePeriods([]);
    setSelectedPeriod(null);
  };

  const loadAvailablePeriods = async (origins) => {
    if (origins.length < 2) return;
    const generation = viewGeneration.current;
    setLoadingPeriods(true);
    try {
      const [periodsResult, availabilityResult] = await Promise.all([
        APIService.getComparisonPeriods(origins),
        APIService.checkHistoryAvailability(origins)
      ]);
      if (generation !== viewGeneration.current) return;
      setAvailablePeriods(periodsResult.periods || []);
      setHistoryAvailability(availabilityResult.origins || []);
      if (periodsResult.periods?.length > 0) {
        setSelectedPeriod(periodsResult.periods[0]);
      } else {
        setSelectedPeriod(null);
      }
    } catch (err) {
      if (generation !== viewGeneration.current) return;
      console.error('Failed to load periods:', err);
      setAvailablePeriods([]);
      setHistoryAvailability([]);
      setSelectedPeriod(null);
    } finally {
      if (generation === viewGeneration.current) setLoadingPeriods(false);
    }
  };

  const syncHistoryForDomain = async (origin) => {
    const generation = viewGeneration.current;
    setSyncingHistory(origin);
    try {
      await APIService.syncHistoryForOrigin(origin);
      if (generation !== viewGeneration.current) return;
      await loadAvailablePeriods(selectedForCompare);
    } catch (err) {
      if (generation !== viewGeneration.current) return;
      setError(err.message);
    } finally {
      if (generation === viewGeneration.current) setSyncingHistory(null);
    }
  };

  const runComparison = async () => {
    if (selectedForCompare.length < 2) {
      setError('Vyberte alespoň 2 domény pro porovnání');
      return;
    }
    const generation = ++viewGeneration.current;
    const soundToken = auditSound.current.start(true);
    
    const normalizedDevice = normalizeDeviceForSource(dataSource, selectedDevice);
    if (normalizedDevice !== selectedDevice) {
      setSelectedDevice(normalizedDevice);
    }
    
    setLoading(true);
    setError(null);
    setSyncProgress(null);
    
    const formFactor = normalizedDevice === 'ALL' ? null : normalizedDevice;
    
    try {
      let results;
      if (comparisonMode === 'historical' && selectedPeriod) {
        const estimate = await APIService.estimateSyncTime(selectedForCompare);
        if (generation !== viewGeneration.current) return;
        
        if (estimate.originsNeedingSync > 0) {
          setSyncProgress({
            status: 'syncing',
            message: `Načítám historická data pro ${estimate.originsNeedingSync} ${estimate.originsNeedingSync === 1 ? 'doménu' : estimate.originsNeedingSync < 5 ? 'domény' : 'domén'}...`,
            estimatedTime: estimate.estimatedTimeText,
            missingOrigins: estimate.missingOrigins
          });
          setBatchSyncing(true);
          
          try {
            const syncResult = await APIService.batchSyncHistory(estimate.missingOrigins, formFactor);
            if (generation !== viewGeneration.current) return;
            
            if (syncResult.failedOrigins?.length > 0) {
              const failedNames = syncResult.failedOrigins.map(f => f.origin).join(', ');
              setSyncProgress({
                status: 'partial',
                message: `Upozornění: Pro některé domény nejsou data dostupná: ${failedNames}`
              });
              
              if (syncResult.syncedOrigins?.length === 0) {
                auditSound.current.cancel(soundToken);
                setLoading(false);
                setBatchSyncing(false);
                return;
              }
            } else {
              setSyncProgress({
                status: 'complete',
                message: `Synchronizováno ${syncResult.syncedOrigins?.length || 0} domén`
              });
            }
            
            await loadAvailablePeriods(selectedForCompare);
            if (generation !== viewGeneration.current) return;
          } catch (syncErr) {
            if (generation !== viewGeneration.current) return;
            auditSound.current.cancel(soundToken);
            setSyncProgress({
              status: 'error',
              message: `Chyba synchronizace: ${syncErr.message}`
            });
            setLoading(false);
            setBatchSyncing(false);
            return;
          } finally {
            if (generation === viewGeneration.current) setBatchSyncing(false);
          }
        }
        
        results = await APIService.getHistoricalComparison(selectedForCompare, selectedPeriod, formFactor);
      } else {
        results = await APIService.compareDomains(selectedForCompare, formFactor, { force: true });
      }
      if (generation !== viewGeneration.current) return;
      console.log('[Comparison] Results received:', results);
      console.log('[Comparison] Results type:', typeof results, Array.isArray(results));
      if (results && results.length > 0) {
        console.log('[Comparison] First result structure:', JSON.stringify(results[0], null, 2).substring(0, 500));
      }
      setComparisonData(results);
      setCompareMode(true);
      if (Array.isArray(results) && results.some(result => result.success === true || result.data?.success === true)) {
        auditSound.current.complete(soundToken);
      }
    } catch (err) {
      auditSound.current.cancel(soundToken);
      if (generation !== viewGeneration.current) return;
      console.error('[Comparison] Error:', err);
      setError(err.message);
    } finally {
      if (generation === viewGeneration.current) setLoading(false);
    }
  };

  const handleReset = () => {
    viewGeneration.current++;
    auditSound.current.cancel();
    setDomain('');
    setCruxData(null);
    setLighthouseData(null);
    setLabData(null);
    setHistoryData(null);
    setMonthlyData(null);
    setComparisonData(null);
    setComparisonMode('current');
    setAvailablePeriods([]);
    setSelectedPeriod(null);
    setHistoryAvailability([]);
    setSyncingHistory(null);
    setBatchSyncing(false);
    setSyncProgress(null);
    setError(null);
    setCompareMode(false);
    setSelectedDevice('ALL');
    setAnalysisType('origin');
    setViewMode('executive');
    setActiveSection('prehled');
    setHistorySelectedMetric('LCP');
    setHistoryExpanded(false);
    setLastSavedAnalysisId(null);
    setActivePlaybook(null);
    setLoading(false);
    setLoadingPeriods(false);
    setLabTimeoutError(null);
    setLabInitialSubTab(null);
    setPendingScrollAnchor(null);
    setFormFactorFractions(null);
    setLcpImageSubparts(null);
    setNavigationTypes(null);
    setLcpResourceType(null);
    setCollectionPeriod(null);
    setSelectedForCompare([]);
    setSelectedMetric('LCP');
    setCurrentMetrics(null);
    setIsSharedView(false);
    setSharedInfo(null);
    setHistoricalData(null);
    setDataSource('crux');
    setSearchFormSource('crux');
    setHadPreviousResults(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getMetrics = useCallback(() => {
    if (!cruxData?.byFormFactor) return null;
    const deviceData = cruxData.byFormFactor[selectedDevice] || cruxData.byFormFactor.ALL;
    return deviceData?.metrics || null;
  }, [cruxData, selectedDevice]);

  const metrics = getMetrics();

  // Calculate device statuses separately for Core Web Vitals and Other Metrics
  const { coreVitalsDeviceStatuses, otherMetricsDeviceStatuses } = useMemo(() => {
    if (!cruxData?.byFormFactor) return { coreVitalsDeviceStatuses: {}, otherMetricsDeviceStatuses: {} };
    
    // Thresholds for metrics (good, needs-improvement boundaries)
    const CORE_VITALS_THRESHOLDS = {
      LCP: [2500, 4000],
      INP: [200, 500],
      CLS: [0.1, 0.25]
    };
    
    const OTHER_METRICS_THRESHOLDS = {
      FCP: [1800, 3000],
      TTFB: [800, 1800],
      RTT: [75, 275],
      FID: [100, 300]
    };
    
    const getWorstStatus = (metrics, thresholds) => {
      if (!metrics) return null;
      let worstStatus = 'good';
      
      for (const [metricName, [goodThreshold, poorThreshold]] of Object.entries(thresholds)) {
        const metric = metrics[metricName];
        if (!metric) continue;
        
        const value = metric.value ?? metric.p75 ?? metric.percentiles?.p75;
        if (value == null) continue;
        
        let status = 'good';
        if (value <= goodThreshold) {
          status = 'good';
        } else if (value <= poorThreshold) {
          status = 'needs-improvement';
        } else {
          status = 'poor';
        }
        
        if (status === 'poor') {
          worstStatus = 'poor';
          break;
        } else if (status === 'needs-improvement' && worstStatus === 'good') {
          worstStatus = 'needs-improvement';
        }
      }
      
      return worstStatus;
    };
    
    const coreStatuses = {};
    const otherStatuses = {};
    const devices = ['ALL', 'PHONE', 'DESKTOP', 'TABLET'];
    
    for (const device of devices) {
      const deviceData = cruxData.byFormFactor[device];
      if (deviceData?.metrics && !deviceData.error) {
        coreStatuses[device] = getWorstStatus(deviceData.metrics, CORE_VITALS_THRESHOLDS);
        otherStatuses[device] = getWorstStatus(deviceData.metrics, OTHER_METRICS_THRESHOLDS);
      }
    }
    
    return { coreVitalsDeviceStatuses: coreStatuses, otherMetricsDeviceStatuses: otherStatuses };
  }, [cruxData]);

  // One-click audit: failing Core Web Vitals per device (CrUX field data only)
  const failingVitals = useMemo(() => detectFailingVitals(cruxData), [cruxData]);
  // Only an explicitly verified origin result may supply origin trend context.
  const diagnosticOrigin = useMemo(() => {
    if (loading || dataSource !== 'crux' || !['origin', 'url_fallback_origin'].includes(cruxData?.queryMode)) return null;
    try {
      const target = cruxData.usedTarget;
      if (!target) return null;
      return new URL(/^https?:\/\//i.test(target) ? target : `https://${target}`).origin;
    } catch { return null; }
  }, [cruxData, dataSource, loading]);
  const diagnosticHistory = useDiagnosticHistory(diagnosticOrigin);
  const metricTrends = useMemo(() => detectMetricTrends(diagnosticHistory.response), [diagnosticHistory.response]);
  const diagnosticCandidates = useMemo(() => buildDiagnosticCandidates(cruxData?.byFormFactor, metricTrends), [cruxData, metricTrends]);
  const [diagnosticSelection, setDiagnosticSelection] = useState(null);
  useEffect(() => { setDiagnosticSelection(null); }, [cruxData, dataSource]);
  const openTrendDiagnostic = (item) => {
    const currentPair = diagnosticCandidates.find(candidate =>
      candidate.metric === item.metric && candidate.device === item.device);
    setDiagnosticSelection({
      ...item,
      ...currentPair,
      // Keep the current field value (including null), not a historical sample.
      // The selected trend's historical values stay in auditContext.trend.
      reason: 'trend',
      trend: item.trend
    });
  };

  if (emailVerificationStatus !== null) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-slate-950">
        <div className="glass-card p-8 max-w-md w-full text-center">
          {emailVerificationStatus.success ? (
            <>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-500/20 flex items-center justify-center">
                <svg className="w-10 h-10 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-white mb-4">{t('emailVerified.title')}</h1>
              <p className="text-slate-400 mb-6">{emailVerificationStatus.message}</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={() => { window.location.href = '/?openPricing=true'; }}
                  className="px-6 py-3 rounded-lg bg-gradient-to-r from-blue-500 to-purple-500 text-white font-semibold hover:opacity-90 transition-opacity"
                >
                  {t('emailVerified.pickPlan')}
                </button>
                <button
                  onClick={() => { window.location.href = '/'; }}
                  className="px-6 py-3 rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-500 transition-colors"
                >
                  {t('emailVerified.continueFree')}
                </button>
                <button
                  onClick={() => { window.location.href = '/'; }}
                  className="px-6 py-3 rounded-lg bg-slate-700 text-white font-semibold hover:bg-slate-600 transition-colors"
                >
                  {t('emailVerified.goToLogin')}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-500/20 flex items-center justify-center">
                <svg className="w-10 h-10 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-white mb-4">Ověření selhalo</h1>
              <p className="text-slate-400 mb-6">{emailVerificationStatus.message}</p>
              <button
                onClick={() => { window.location.href = '/'; }}
                className="px-6 py-3 rounded-lg bg-slate-700 text-white font-semibold hover:bg-slate-600 transition-colors"
              >
                Zpět na hlavní stránku
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  if (resetPasswordToken) {
    return <ResetPasswordPage token={resetPasswordToken} onSuccess={() => {
      setResetPasswordToken(null);
      window.history.replaceState({}, '', window.location.pathname);
      setAuthMode('login');
      setShowAuthModal(true);
    }} />;
  }

  const isPageHidden = (path) => hiddenPages[path] === true;

  const sidebarNavigate = (path) => {
    const pageMap = {
      '/': 'home',
      '/admin': 'admin',
      '/global-insights': 'global-insights'
    };
    const page = pageMap[path] || 'home';
    navigateTo(page, path);
  };

  if (currentPage === 'admin') {
    return (
      <Suspense fallback={<LazyFallback />}>
        <AdminPage user={currentUser} onOpenAuth={(mode) => { setAuthMode(mode || 'login'); setShowAuthModal(true); }} onNavigateHome={() => navigateTo('home', '/')} />
        {showAuthModal && <AuthModal isOpen={showAuthModal} mode={authMode}
          onClose={() => setShowAuthModal(false)} onLogin={handleLogin} onModeChange={setAuthMode} />}
      </Suspense>
    );
  }

  if (currentPage === 'global-insights') {
    if (currentUser?.tier !== 'admin') {
      return (
        <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
          <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-8 max-w-md text-center">
            <span className="text-4xl mb-4 block">🔒</span>
            <h1 className="text-xl font-bold text-white mb-2">Pouze pro administrátory</h1>
            <p className="text-gray-400 mb-4">Global Insights je interní nástroj dostupný pouze pro administrátory.</p>
            <button 
              onClick={() => navigateTo('home', '/')}
              className="px-4 py-2 bg-gradient-to-r from-purple-600 to-cyan-600 text-white rounded-lg hover:opacity-90"
            >
              Zpět na Dashboard
            </button>
          </div>
        </div>
      );
    }
    return (
      <SidebarLayout
        currentPage="global-insights"
        currentUser={currentUser}
        onNavigate={sidebarNavigate}
        onOpenAuth={(mode) => { setAuthMode(mode); setShowAuthModal(true); }}
        onLogout={handleLogout}
      >
        <Suspense fallback={<LazyFallback />}>
          <GlobalInsightsPage currentUser={currentUser} navigateTo={navigateTo} />
        </Suspense>
      </SidebarLayout>
    );
  }

  const pageLayoutProps = {
    currentUser,
    onLoginClick: () => { setAuthMode('login'); setShowAuthModal(true); },
    onLogout: handleLogout,
    onPricingClick: () => {
      window.location.href = language === 'en' ? '/pricing?lang=en' : '/pricing';
    },
    onNewAnalysis: handleReset,
    onNavigateHome: handleReset,
    onCompareMode: setCompareMode,
    onNavigateTo: navigateTo,
    onShowBadges: openBadges,
    onShowAgency: openAgency,
    auditSoundEnabled,
    onAuditSoundChange: toggleAuditSound,
    apiStatus
  };

  const renderAdminPanel = () => currentUser?.is_admin && showAdminPanel ? (
    <Suspense fallback={<LazyFallback />}>
      <AdminPanel
        isOpen={showAdminPanel}
        onClose={() => setShowAdminPanel(false)}
      />
    </Suspense>
  ) : null;

  const renderModals = () => (
    <>
      {renderAdminPanel()}
      {showAuthModal && (
        <Suspense fallback={<LazyFallback />}>
          <AuthModal 
            isOpen={showAuthModal}
            mode={authMode}
            onClose={() => setShowAuthModal(false)}
            onLogin={handleLogin}
            onModeChange={setAuthMode}
          />
        </Suspense>
      )}
      {showPricingModal && (
        <Suspense fallback={<LazyFallback />}>
          <PricingModal
            onClose={() => setShowPricingModal(false)}
            currentUser={currentUser}
            onOpenAuth={(mode) => {
              setShowPricingModal(false);
              setAuthMode(mode || 'register');
              setShowAuthModal(true);
            }}
          />
        </Suspense>
      )}
      {showAiNarrativeSettings && (
        <Suspense fallback={<LazyFallback />}>
          <AiNarrativeSettingsModal
            user={currentUser}
            onClose={() => setShowAiNarrativeSettings(false)}
          />
        </Suspense>
      )}
      {showAlertSettings && (
        <div className="cv-app-portal fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowAlertSettings(false)}
          />
          <div 
            className="relative bg-slate-900/95 backdrop-blur-md rounded-xl border border-slate-700/50 w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-700 px-4 py-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <svg className="w-4 h-4 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                </div>
                <h2 className="text-lg font-semibold text-white">Monitoring výkonnosti</h2>
              </div>
              <button
                onClick={() => setShowAlertSettings(false)}
                className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-700/50"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4">
              <Suspense fallback={<div className="flex items-center justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div></div>}>
                <AlertSettings 
                  user={currentUser}
                  origins={[...new Set([
                    ...(trackedOrigins || []).map(o => typeof o === 'string' ? o : o.origin),
                    ...(userDomains || []).map(d => d.origin)
                  ])].filter(Boolean)}
                  domain={cruxData?.usedTarget || domain}
                  onClose={() => { setShowAlertSettings(false); setAlertSettingsDefaultTab(null); }}
                  onOpenGuide={() => {
                    setShowAlertSettings(false);
                    setAlertSettingsDefaultTab(null);
                    openIntegrationGuide();
                  }}
                  defaultTab={alertSettingsDefaultTab}
                />
              </Suspense>
            </div>
          </div>
        </div>
      )}
    </>
  );

  if (comparisonReportToken !== null) {
    return (
      <PageLayout {...pageLayoutProps} modals={renderModals()}>
        <Suspense fallback={<div role="status" className="mx-auto h-96 max-w-7xl animate-pulse rounded-xl bg-slate-800/50" aria-label={language === 'cs' ? 'Načítám report' : 'Loading report'} />}>
          <SharedComparisonPage
            token={comparisonReportToken}
            user={currentUser}
            onOpenAuth={(mode) => { setAuthMode(mode || 'login'); setShowAuthModal(true); }}
          />
        </Suspense>
      </PageLayout>
    );
  }

  if (currentPage === 'audit') {
    return (
      <Suspense fallback={<LazyFallback />}>
        <AuditPage {...pageLayoutProps} modals={renderModals()} />
      </Suspense>
    );
  }

  if (currentPage === 'crux-alternativa') {
    if (isPageHidden('/web-core-vitals-google-report-crux')) return <NotFoundPage onNavigateHome={() => navigateTo('home', '/')} />;
    return (
      <PageLayout {...pageLayoutProps} modals={renderModals()} noMainWrapper>
        <Suspense fallback={<LazyFallback />}>
          <CruxDashboardAlternativa onNavigateHome={() => navigateTo('home', '/')} />
        </Suspense>
      </PageLayout>
    );
  }

  if (currentPage === 'terms') {
    if (isPageHidden('/terms')) return <NotFoundPage onNavigateHome={() => navigateTo('home', '/')} />;
    return (
      <PageLayout {...pageLayoutProps} modals={renderModals()}>
        <TermsPage showHeader={false} />
      </PageLayout>
    );
  }

  if (currentPage === 'privacy') {
    if (isPageHidden('/privacy')) return <NotFoundPage onNavigateHome={() => navigateTo('home', '/')} />;
    return (
      <PageLayout {...pageLayoutProps} modals={renderModals()}>
        <PrivacyPage showHeader={false} />
      </PageLayout>
    );
  }

  if (currentPage === 'dpa') {
    if (isPageHidden('/dpa')) return <NotFoundPage onNavigateHome={() => navigateTo('home', '/')} />;
    return (
      <PageLayout {...pageLayoutProps} modals={renderModals()}>
        <DpaPage showHeader={false} />
      </PageLayout>
    );
  }

  if (currentPage === 'credits') {
    if (isPageHidden('/credits')) return <NotFoundPage onNavigateHome={() => navigateTo('home', '/')} />;
    return (
      <PageLayout {...pageLayoutProps} modals={renderModals()}>
        <CreditsPage showHeader={false} />
      </PageLayout>
    );
  }

  if (currentPage === 'ranking' || currentPage === 'rankings') {
    if (isPageHidden('/rankings')) return <NotFoundPage onNavigateHome={() => navigateTo('home', '/')} />;
    return (
      <PageLayout {...pageLayoutProps} modals={renderModals()} noMainWrapper>
        <RankingsPage navigateTo={navigateTo} currentUser={currentUser} />
      </PageLayout>
    );
  }

  if (currentPage === 'top-performers') {
    if (isPageHidden('/top-performers')) return <NotFoundPage onNavigateHome={() => navigateTo('home', '/')} />;
    return (
      <PageLayout {...pageLayoutProps} modals={renderModals()} noMainWrapper>
        <TopPerformersPage navigateTo={navigateTo} currentUser={currentUser} />
      </PageLayout>
    );
  }

  if (currentPage === 'czech-webs-ranking') {
    if (isPageHidden('/czech-webs-ranking')) return <NotFoundPage onNavigateHome={() => navigateTo('home', '/')} />;
    return (
      <PageLayout {...pageLayoutProps} modals={renderModals()} noMainWrapper>
        <CzechWebsRankingPage navigateTo={navigateTo} currentUser={currentUser} />
      </PageLayout>
    );
  }

  if (currentPage === 'monitoring') {
    if (isPageHidden('/monitoring')) return <NotFoundPage onNavigateHome={() => navigateTo('home', '/')} />;
    return (
      <PageLayout {...pageLayoutProps} modals={renderModals()} noMainWrapper>
        <MonitoringPage onBack={() => navigateTo('home')} onNavigateTo={navigateTo} />
      </PageLayout>
    );
  }

  if (currentPage === 'platform-status') {
    if (isPageHidden('/platform-status')) return <NotFoundPage onNavigateHome={() => navigateTo('home', '/')} />;
    return (
      <PageLayout {...pageLayoutProps} modals={renderModals()} noMainWrapper>
        <PlatformStatusPage />
      </PageLayout>
    );
  }

  if (currentPage === 'ai-crawler-guide') {
    if (isPageHidden('/ai-crawler-guide')) return <NotFoundPage onNavigateHome={() => navigateTo('home', '/')} />;
    return (
      <PageLayout {...pageLayoutProps} modals={renderModals()} noMainWrapper>
        <AiCrawlerGuidePage />
      </PageLayout>
    );
  }

  if (currentPage === 'blog') {
    if (isPageHidden('/blog')) return <NotFoundPage onNavigateHome={() => navigateTo('home', '/')} />;
    return (
      <PageLayout {...pageLayoutProps} modals={renderModals()} noMainWrapper>
        <BlogPage key={window.location.pathname} onNavigateTo={navigateTo} />
      </PageLayout>
    );
  }

  if (currentPage === 'blog-article') {
    if (isPageHidden('/blog')) return <NotFoundPage onNavigateHome={() => navigateTo('home', '/')} />;
    const slug = window.location.pathname.replace('/blog/', '');
    return (
      <PageLayout {...pageLayoutProps} modals={renderModals()} noMainWrapper>
        <ArticlePage slug={slug} onNavigateTo={navigateTo} />
      </PageLayout>
    );
  }

  if (currentPage === 'client-dashboard') {
    return <ClientDashboardPage />;
  }

  if (currentPage === 'quick-audit') {
    if (isPageHidden('/quick-audit')) return <NotFoundPage onNavigateHome={() => navigateTo('home', '/')} />;
    return (
      <PageLayout {...pageLayoutProps} modals={renderModals()} noMainWrapper>
        <QuickAuditPage 
          onBack={() => navigateTo('home')}
          onStartAudit={() => {
            navigateTo('home');
            handleReset();
            setTimeout(() => {
              const heroSearch = document.querySelector('#hero-search input, .hero-section input');
              if (heroSearch) heroSearch.focus();
            }, 100);
          }}
        />
      </PageLayout>
    );
  }

  if (currentPage === 'quick-tools') {
    if (isPageHidden('/quick-tools')) return <NotFoundPage onNavigateHome={() => navigateTo('home', '/')} />;
    return (
      <PageLayout {...pageLayoutProps} modals={renderModals()} noMainWrapper>
        <QuickToolsPage 
          onBack={() => navigateTo('home')}
          onNavigateTo={navigateTo}
        />
      </PageLayout>
    );
  }

  if (currentPage === 'eshop-health-check') {
    return (
      <PageLayout {...pageLayoutProps} modals={renderModals()} noMainWrapper>
        <EshopHealthCheckPage
          onPricingClick={() => {
            window.location.href = language === 'en' ? '/pricing?lang=en' : '/pricing';
          }}
        />
      </PageLayout>
    );
  }

  if (currentPage === 'lab-insights') {
    if (isPageHidden('/lab-insights')) return <NotFoundPage onNavigateHome={() => navigateTo('home', '/')} />;
    return (
      <PageLayout {...pageLayoutProps} modals={renderModals()} noMainWrapper>
        <LabInsightsPage 
          onBack={() => navigateTo('home')}
          onStartAnalysis={() => {
            // Take the user to /lab-test where they enter a URL; that page's
            // "Spustit Lab Test" button then deep-links into the homepage
            // with ?origin=...&tab=lab which auto-triggers the Lab analysis
            // (Detailní analýza, Mobil) via the handler in App.jsx.
            navigateTo('lab-test', '/lab-test');
          }}
        />
      </PageLayout>
    );
  }

  if (currentPage === 'lab-test') {
    if (isPageHidden('/lab-test')) return <NotFoundPage onNavigateHome={() => navigateTo('home', '/')} />;
    return (
      <PageLayout {...pageLayoutProps} modals={renderModals()} noMainWrapper>
        <LabTestPage 
          onBack={() => { navigateTo('home'); handleReset(); }}
          user={currentUser}
          onShowPricing={() => setShowPricingModal(true)}
          onShowLogin={() => { setAuthMode('login'); setShowAuthModal(true); }}
          onRunLab={(url) => {
            // Lab and multi-domain comparison share the homepage results area,
            // but never the active measurement mode.
            setCompareMode(false);
            setComparisonData(null);
            setComparisonMode('current');
            navigateTo('home', '/');
            handleAnalyze(url, 'origin', { source: 'lab', device: 'PHONE', userInitiated: true });
          }}
        />
      </PageLayout>
    );
  }

  if (currentPage === 'history-monitoring') {
    if (isPageHidden('/history-monitoring')) return <NotFoundPage onNavigateHome={() => navigateTo('home', '/')} />;
    return (
      <PageLayout {...pageLayoutProps} modals={renderModals()} noMainWrapper>
        <HistoryMonitoringPage 
          onBack={() => navigateTo('home')}
          onStartAudit={() => {
            navigateTo('home');
            setTimeout(() => {
              const heroSearch = document.querySelector('#hero-search input, .hero-section input');
              if (heroSearch) heroSearch.focus();
            }, 100);
          }}
        />
      </PageLayout>
    );
  }

  if (currentPage === 'competitor-comparison') {
    if (isPageHidden('/competitor-comparison')) return <NotFoundPage onNavigateHome={() => navigateTo('home', '/')} />;
    return (
      <PageLayout {...pageLayoutProps} modals={renderModals()} noMainWrapper>
        <CompetitorComparisonPage 
          onBack={() => navigateTo('home')}
          onStartComparison={() => {
            navigateTo('domain-comparison', language === 'en' ? '/domain-comparison?lang=en' : '/domain-comparison');
          }}
        />
      </PageLayout>
    );
  }

  if (currentPage === 'methodology') {
    if (isPageHidden('/methodology')) return <NotFoundPage onNavigateHome={() => navigateTo('home', '/')} />;
    return (
      <PageLayout {...pageLayoutProps} modals={renderModals()} noMainWrapper>
        <MeasurementMethodologyPage 
          onBack={() => navigateTo('home')}
          onStartAudit={() => {
            navigateTo('home');
            setTimeout(() => {
              const heroSearch = document.querySelector('#hero-search input, .hero-section input');
              if (heroSearch) heroSearch.focus();
            }, 100);
          }}
        />
      </PageLayout>
    );
  }

  if (currentPage === 'market-insights') {
    if (isPageHidden('/market-insights')) return <NotFoundPage onNavigateHome={() => navigateTo('home', '/')} />;
    return (
      <PageLayout {...pageLayoutProps} modals={renderModals()} noMainWrapper>
        <MarketInsightsPage />
      </PageLayout>
    );
  }

  if (currentPage === 'homepage-archive') {
    return (
      <PageLayout {...pageLayoutProps} modals={renderModals()}>
        <div className="text-center space-y-6 mb-8">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold theme-text mb-3 animate-fade-in leading-tight">
            {t('hero.title')}
          </h1>
          <p className="theme-text-secondary text-lg md:text-xl max-w-2xl mx-auto animate-fade-in" style={{ textWrap: 'balance' }}>
            {t('hero.subtitle')}
          </p>
        </div>
        <SearchForm 
          onAnalyze={handleAnalyze}
          onCompare={handleCompare}
          loading={loading}
          hasResults={false}
          compareMode={false}
          onReset={handleReset}
        />
        <ValueProposition />
        <IntegrationsLogos />
        <HowItWorks />
        <TargetAudience />
        <QuickToolsGrid onNavigateTo={navigateTo} />
        <ProductShowcase />
        <PlaybookPreview />
        <PricingPreview
          onPricingClick={() => setShowPricingModal(true)}
          pricingHref={language === 'en' ? '/pricing?lang=en' : '/pricing'}
        />
        <TopPerformersPreview navigateTo={navigateTo} />
        <AgencyPreview />
        <FAQ />
        <FinalCTA onNewAnalysis={() => window.scrollTo({ top: 0, behavior: 'smooth' })} />
      </PageLayout>
    );
  }

  if (currentPage === 'checkout' || currentPage === 'checkout-success' || currentPage === 'checkout-cancel') {
    const urlParams = new URLSearchParams(window.location.search);
    const tier = urlParams.get('tier') || 'pro';
    const cycle = urlParams.get('cycle') || 'monthly';
    // Prefer the dedicated route; fall back to the legacy ?status= query for
    // any old links still in the wild.
    const legacyStatus = urlParams.get('status');
    const isSuccess = currentPage === 'checkout-success' || legacyStatus === 'success';
    const isCancel = currentPage === 'checkout-cancel' || legacyStatus === 'cancel';
    const tierLabel = tier.charAt(0).toUpperCase() + tier.slice(1);
    const cycleLabel = cycle === 'yearly'
      ? (language === 'cs' ? 'roční' : 'yearly')
      : (language === 'cs' ? 'měsíční' : 'monthly');

    return (
      <div className="cv-public min-h-screen theme-bg theme-text flex items-center justify-center p-4">
        <div className="glass-card p-8 max-w-lg w-full text-center">
          <div className={`w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center ${
            isSuccess ? 'bg-gradient-to-r from-emerald-500 to-teal-500'
            : isCancel ? 'bg-gradient-to-r from-amber-500 to-orange-500'
            : 'bg-gradient-to-r from-blue-500 to-purple-500'
          }`}>
            {isSuccess ? (
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : isCancel ? (
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M5 19h14a2 2 0 001.732-3L13.732 4a2 2 0 00-3.464 0L3.27 16A2 2 0 005 19z" />
              </svg>
            ) : (
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
            )}
          </div>

          {isSuccess && (
            <>
              <h1 className="text-2xl font-bold text-white mb-3">
                {language === 'cs' ? 'Platba proběhla úspěšně' : 'Payment successful'}
              </h1>
              <p className="text-slate-300 mb-2">
                {language === 'cs'
                  ? `Děkujeme za nákup tarifu ${tierLabel} (${cycleLabel}).`
                  : `Thank you for subscribing to the ${tierLabel} plan (${cycleLabel}).`}
              </p>
              <p className="text-slate-400 text-sm mb-6">
                {language === 'cs'
                  ? 'Aktivace proběhne během několika sekund. Pokud se tarif neaktivuje, obnovte stránku za chvíli.'
                  : 'Activation completes within a few seconds. If your plan is not active, refresh the page in a moment.'}
              </p>
              <button
                onClick={async () => {
                  try {
                    const resp = await APIService.checkSession();
                    const fresh = resp?.user || (resp && resp.id ? resp : null);
                    if (fresh) {
                      AuthStorage.setUser(fresh);
                      setCurrentUser(fresh);
                    }
                  } catch (e) {}
                  navigateTo('home', '/');
                }}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-semibold rounded-lg hover:opacity-90 transition-all"
              >
                {language === 'cs' ? 'Pokračovat do aplikace' : 'Continue to app'}
              </button>
            </>
          )}

          {isCancel && (
            <>
              <h1 className="text-2xl font-bold text-white mb-3">
                {language === 'cs' ? 'Platba zrušena' : 'Payment cancelled'}
              </h1>
              <p className="text-slate-400 mb-6">
                {language === 'cs'
                  ? 'Žádná částka vám nebyla stržena. Můžete se kdykoli vrátit a vybrat jiný tarif.'
                  : 'You were not charged. You can return any time and choose a different plan.'}
              </p>
              <button
                onClick={() => navigateTo('home', '/')}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold rounded-lg hover:opacity-90 transition-all"
              >
                {language === 'cs' ? 'Zpět na hlavní stránku' : 'Back to homepage'}
              </button>
            </>
          )}

          {!isSuccess && !isCancel && (
            <>
              <h1 className="text-2xl font-bold text-white mb-4">
                {language === 'cs' ? 'Aktivace tarifu' : 'Plan Activation'}
              </h1>
              <p className="text-slate-400 mb-6">
                {language === 'cs'
                  ? `Připravujeme platební bránu pro tarif ${tierLabel} (${cycleLabel}).`
                  : `Preparing payment for the ${tierLabel} plan (${cycleLabel}).`}
              </p>
              <button
                onClick={() => navigateTo('home', '/')}
                className="text-blue-400 hover:text-blue-300 transition-colors"
              >
                {language === 'cs' ? '← Zpět na hlavní stránku' : '← Back to homepage'}
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  if (currentPage === 'pricing') {
    return (
      <PageLayout {...pageLayoutProps} modals={renderModals()} noMainWrapper>
        <PricingPage onChoosePlan={() => setShowPricingModal(true)} />
      </PageLayout>
    );
  }

  if (currentPage === '404') {
    return <NotFoundPage />;
  }

  // Show maintenance page for non-admin users when maintenance mode is active
  if (maintenanceChecked && maintenanceMode && currentPage !== 'admin' && !currentUser?.is_admin) {
    return (
      <MaintenancePage 
        message={maintenanceInfo.message} 
        estimatedTime={maintenanceInfo.estimatedTime}
        onAdminLogin={(user) => {
          setCurrentUser(user);
          setMaintenanceMode(false);
        }}
      />
    );
  }

  return (
    <div className="cv-app min-h-screen theme-bg theme-text relative z-0">
      <InteractiveBackground />
      
      <Header 
        currentUser={currentUser}
        onLoginClick={() => { setAuthMode('login'); setShowAuthModal(true); }}
        onLogout={handleLogout}
        onPricingClick={() => setShowPricingModal(true)}
        onNewAnalysis={handleReset}
        onNavigateHome={handleReset}
        onCompareMode={setCompareMode}
        onNavigateTo={navigateTo}
        onShowBadges={openBadges}
        onShowAgency={openAgency}
        auditSoundEnabled={auditSoundEnabled}
        onAuditSoundChange={toggleAuditSound}
        showHeaderLogo={showHeaderLogo}
      />
      
      <div className="nav-spacer"></div>
      
      {!compareMode && (
        <StickySubNav 
          domain={domain}
          usedTarget={cruxData?.usedTarget || labData?.url}
          selectedDevice={selectedDevice}
          onDeviceChange={handleFormFactorChange}
          visible={showStickyNav}
          isLoggedIn={!!currentUser}
          onShare={() => setShowExportModal(true)}
          onOpenMonitoring={openAlertSettings}
          onOpenLabTest={openLabTest}
          onSaveAnalysis={!labData && (cruxData || lighthouseData) ? handleSaveCruxAnalysis : undefined}
          savingAnalysis={savingAnalysis}
          savedAnalysis={!!lastSavedAnalysisId}
          dataSource={dataSource}
          fromCache={cruxData?.fromCache || false}
          tierSlug={userStatus?.tier}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          isLabMode={!!labData}
        />
      )}
      
      {userStatus && (cruxData || lighthouseData || comparisonData || labData || hadPreviousResults) && (
        <div className="max-w-7xl mx-auto px-4 mt-4">
          {(userStatus.quotas?.crux?.percentage >= 100 || userStatus.quotas?.pagespeed?.percentage >= 100 || userStatus.quotas?.domains?.percentage >= 100) && (
            <div id="limit-exhausted-banner">
              <LimitExhaustedBanner 
                status={userStatus} 
                onUpgradeClick={() => setShowPricingModal(true)} 
              />
            </div>
          )}
          
          {(userStatus.restriction || userStatus.warnings?.length > 0) && (
            <div className="mt-2">
              <RestrictionBanner 
                status={userStatus} 
                onUpgradeClick={() => setShowPricingModal(true)} 
              />
            </div>
          )}
        </div>
      )}

      <main data-export-root="dashboard" className={`relative z-10 ${
        (!cruxData && !lighthouseData && !comparisonData && !labData && !hadPreviousResults && !loading && currentPage !== 'health-check')
          ? '' 
          : 'max-w-7xl mx-auto px-4 py-8'
      }`}>
        {currentPage === 'health-check' ? (
          <HealthCheckPage 
            key={window.location.search} 
            navigateTo={navigateTo} 
            currentUser={currentUser} 
            onOpenAuth={(mode) => { setShowAuthModal(true); setAuthMode(mode); }} 
            onOpenPricing={() => setShowPricingModal(true)} 
            embedded={true} 
          />
        ) : (
        <>
        {/* Show SearchForm when there are results, during loading, or when compare mode is active */}
        {(cruxData || lighthouseData || comparisonData || labData || hadPreviousResults || loading || compareMode) && (
        <SearchForm 
          onAnalyze={handleAnalyze}
          onCompare={handleCompare}
          loading={loading}
          hasResults={!!cruxData || !!lighthouseData || !!comparisonData || hadPreviousResults}
          compareMode={compareMode}
          onCompareModeChange={(enabled) => {
            if (enabled) return enterCompareMode();
            if (currentPage === 'domain-comparison') {
              navigateTo('home', language === 'en' ? '/?lang=en' : '/');
              handleReset();
            } else {
              setCompareMode(false);
            }
          }}
          onReset={handleReset}
          currentDomain={domain}
          borderAnimationEnabled={borderAnimationEnabled}
          borderAnimSettings={borderAnimSettings}
          bgAnimationEnabled={bgAnimationEnabled}
          bgAnimSettings={bgAnimSettings}
          selectedForCompare={selectedForCompare}
          setSelectedForCompare={setSelectedForCompare}
          historyAvailability={historyAvailability}
          currentUser={currentUser}
          userDomains={userDomains}
          userDomainGroups={userDomainGroups}
          trackedOrigins={trackedOrigins}
          comparisonMode={comparisonMode}
          setComparisonMode={setComparisonMode}
          availablePeriods={availablePeriods}
          selectedPeriod={selectedPeriod}
          setSelectedPeriod={setSelectedPeriod}
          loadingPeriods={loadingPeriods}
          syncingHistory={syncingHistory}
          syncProgress={syncProgress}
          batchSyncing={batchSyncing}
          syncHistoryForDomain={syncHistoryForDomain}
          toggleDomainForCompare={toggleDomainForCompare}
          handleRemoveUserDomain={handleRemoveUserDomain}
          removingDomain={removingDomain}
          runComparison={runComparison}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          source={searchFormSource}
          onSourceChange={setSearchFormSource}
        />
        )}

        {!cruxData && !lighthouseData && !comparisonData && !labData && !compareMode && !hadPreviousResults && !loading && (
          <HomepageVariantB
            onNavigateTo={navigateTo}
            onLoginClick={() => { setAuthMode('login'); setShowAuthModal(true); }}
            onPricingClick={() => setShowPricingModal(true)}
            onAnalyze={(url) => handleAnalyze(url, 'origin', { source: 'crux', device: 'ALL', userInitiated: true })}
            embedded={true}
            loading={loading}
          />
        )}

        {/* Usage Stats Grid - QuotaIndicator + FormFactorFractions side by side on desktop */}
        {!compareMode && (cruxData || lighthouseData) && viewMode !== 'executive' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
            {userStatus && (
              <QuotaIndicator 
                status={userStatus} 
                onUpgradeClick={() => setShowPricingModal(true)}
              />
            )}
            {formFactorFractions && dataSource === 'crux' && (
              <FormFactorFractions 
                fractions={formFactorFractions}
                collectionPeriod={cruxData?.collectionPeriod}
              />
            )}
          </div>
        )}

        {/* Business Tools - Collapsible section with financial calculators */}
        {!compareMode && (cruxData || lighthouseData) && viewMode !== 'executive' && (
          <Suspense fallback={<div className="h-16 bg-gray-800/30 rounded-xl animate-pulse mb-6" />}>
            <BusinessToolsSection
              metrics={{
                LCP: (dataSource === 'crux' ? metrics?.LCP?.value : currentMetrics?.LCP?.value) || null,
                FCP: (dataSource === 'crux' ? metrics?.FCP?.value : currentMetrics?.FCP?.value) || null,
                TTFB: (dataSource === 'crux' ? metrics?.TTFB?.value : currentMetrics?.TTFB?.value) || null,
                INP: (dataSource === 'crux' ? metrics?.INP?.value : currentMetrics?.INP?.value) || null,
                CLS: (dataSource === 'crux' ? metrics?.CLS?.value : currentMetrics?.CLS?.value) || null,
                SI: currentMetrics?.SI?.value || null,
                TBT: currentMetrics?.TBT?.value || null
              }}
              origin={cruxData?.usedTarget || domain}
              historyData={normalizeHistoryForImpactMonitor(historyData)}
              onShowPlaybook={setActivePlaybook}
              lcpMs={(dataSource === 'crux' ? metrics?.LCP?.value : currentMetrics?.LCP?.value) || 0}
            />
          </Suspense>
        )}

        {labTimeoutError && (
          <div className="mb-8 p-6 rounded-2xl bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/30 backdrop-blur-sm">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
                <svg className="w-6 h-6 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-amber-400 mb-2">
                  {t('labTimeout.title', 'Stránka se nenačetla včas')}
                </h3>
                <p className="text-gray-300 mb-3">
                  {labTimeoutError.message}
                </p>
                <div className="bg-gray-800/50 rounded-xl p-4 mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <svg className="w-5 h-5 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="font-medium text-cyan-400">{t('labTimeout.insightTitle', 'Co to znamená?')}</span>
                  </div>
                  <p className="text-gray-400 text-sm">
                    {labTimeoutError.details?.insight}
                  </p>
                </div>
                <div className="flex flex-wrap gap-3">
                  {labTimeoutError.details?.suggestedThrottling && (
                    <button
                      onClick={() => {
                        setLabTimeoutError(null);
                        setError(null);
                        handleAnalyze(labTimeoutError.url, analysisType, { 
                          source: 'lab', 
                          throttling: labTimeoutError.details.suggestedThrottling 
                        });
                      }}
                      className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white font-medium rounded-lg transition-all flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      {t('labTimeout.retryFaster', 'Zkusit s rychlejší sítí')} ({labTimeoutError.details.suggestedThrottlingLabel})
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setLabTimeoutError(null);
                      setError(null);
                    }}
                    className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 font-medium rounded-lg transition-all"
                  >
                    {t('common.dismiss', 'Zavřít')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <AnalysisErrorMessage
          error={labTimeoutError ? null : error}
          dataSource={dataSource}
          onFallbackToPageSpeed={() => {
            setDataSource('pagespeed');
            setSelectedDevice('PHONE');
            setError(null);
            setTimeout(() => handleAnalyze(domain, analysisType, { source: 'pagespeed', device: 'PHONE' }), 100);
          }}
        />

        {comparisonData && Array.isArray(comparisonData) && (
          <Suspense fallback={<LazyFallback />}>
            <ComparisonPanel
              comparisonData={comparisonData}
              originalDomains={selectedForCompare}
              selectedPeriod={selectedPeriod}
              sourceKey={JSON.stringify([comparisonMode, selectedPeriod, selectedForCompare, dataSource])}
              loadDeviceResults={device => comparisonMode === 'historical' && selectedPeriod
                ? APIService.getHistoricalComparison(selectedForCompare, selectedPeriod, device === 'ALL' ? null : device)
                : APIService.compareDomains(selectedForCompare, device)}
              loading={loading}
              selectedDevice={selectedDevice}
              onDeviceChange={handleFormFactorChange}
              onDataRefresh={runComparison}
              user={currentUser}
              onOpenAuth={(mode) => { setAuthMode(mode || 'login'); setShowAuthModal(true); }}
            />
          </Suspense>
        )}

        {/* LCP Image Subparts - CrUX only, not in comparison mode */}
        {lcpImageSubparts && dataSource === 'crux' && !compareMode && (
          <LcpImageSubpartsDisplay lcpImageSubparts={lcpImageSubparts} />
        )}

        {/* Lab Insights Results - show only Lab data when lab test is active */}
        {labData && !compareMode && (
          <div ref={resultsRef} className="space-y-8">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-purple-500/20 text-purple-400">
                <Icons.Flask size={12} /> {t('lab.title')}
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-slate-700 text-slate-300">
                {labData.device === 'mobile' ? <><Icons.Phone size={12} /> {t('lab.devices.mobile')}</> : <><Icons.Desktop size={12} /> {t('lab.devices.desktop')}</>}
              </span>
              <div className="flex-1"></div>
            </div>
            <LabResultsSection 
              labData={labData} 
              origin={labData?.url ? new URL(labData.url).origin : (cruxData?.usedTarget || (domain ? `https://${domain.replace(/^https?:\/\//, '')}` : null))}
              onLoadHistoryTest={handleLoadLabHistoryTest}
              onSaveLabTest={handleSaveLabTest}
              currentUser={currentUser}
              initialActiveTab={labInitialSubTab}
              onInitialActiveTabConsumed={() => setLabInitialSubTab(null)}
            />
          </div>
        )}

        {loading && !cruxData && !lighthouseData && !compareMode && !labData && dataSource !== 'lab' && !hadPreviousResults && (
          <div ref={progressRef}>
            <AnalysisProgressOverlay variant="domain" domain={domain} />
          </div>
        )}

        {loading && compareMode && !comparisonData && (
          <div ref={progressRef}>
            <AnalysisProgressOverlay variant="compare" isCompare={true} domainCount={selectedForCompare?.length || 0} />
          </div>
        )}

        {(cruxData || lighthouseData || hadPreviousResults || (loading && dataSource !== 'lab')) && !compareMode && !labData && dataSource !== 'lab' && (
          <div ref={resultsRef} className="space-y-8">
            <div id="section-prehled" className="flex flex-wrap items-center gap-2 mb-4 scroll-mt-24">
              <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
                dataSource === 'crux' 
                  ? 'bg-green-500/20 text-green-400' 
                  : 'bg-blue-500/20 text-blue-400'
              }`}>
                {dataSource === 'crux' ? <><Icons.Chart size={12} /> CrUX Data</> : <><Icons.Flask size={12} /> PageSpeed Data</>}
              </span>
              {dataSource === 'crux' && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-slate-700 text-slate-300">
                  {selectedDevice === 'ALL' ? <><Icons.AllDevices size={12} /> Všechna zařízení</> : 
                   selectedDevice === 'PHONE' ? <><Icons.Phone size={12} /> Mobil</> : 
                   selectedDevice === 'DESKTOP' ? <><Icons.Desktop size={12} /> Desktop</> : 
                   selectedDevice === 'TABLET' ? <><Icons.Tablet size={12} /> Tablet</> : null}
                </span>
              )}
              <div className="flex-1"></div>
              {dataSource === 'crux' && cruxData && !loading && (
                <Suspense fallback={null}>
                  <QuickAuditButton
                    failingVitals={failingVitals}
                    candidates={diagnosticCandidates}
                    url={cruxData?.usedTarget || domain}
                    user={currentUser}
                    onOpenAuth={(mode) => { setAuthMode(mode); setShowAuthModal(true); }}
                    onOpenPricing={() => setShowPricingModal(true)}
                  />
                </Suspense>
              )}
              <button
                onClick={() => setShowExportModal(true)}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg text-sm text-slate-300 flex items-center gap-2"
              >
                <Icons.Download size={16} />
                Export
              </button>
            </div>

            {dataSource === 'crux' && cruxData && (
              <MetricTrendNotices history={diagnosticHistory} trends={metricTrends} onDiagnose={openTrendDiagnostic} />
            )}
            {diagnosticSelection && (
              <Suspense fallback={null}>
                <MetricDiagnosticModal
                  key={`${diagnosticSelection.metric}:${diagnosticSelection.device}`}
                  failingVitals={failingVitals}
                  candidates={diagnosticCandidates.map(item =>
                    item.metric === diagnosticSelection.metric && item.device === diagnosticSelection.device ? diagnosticSelection : item)}
                  initialSelection={[diagnosticSelection]}
                  url={cruxData?.usedTarget || domain}
                  user={currentUser}
                  onClose={() => setDiagnosticSelection(null)}
                  onOpenAuth={(mode) => { setAuthMode(mode); setShowAuthModal(true); }}
                  onOpenPricing={() => setShowPricingModal(true)}
                />
              </Suspense>
            )}

            {cruxData?.urlFallback && (
              <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-start gap-3">
                <Icons.Info size={18} className="text-amber-400 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-amber-300/90">
                  <span className="font-medium">{cruxData.urlFallbackMessage || 'Pro tuto konkrétní stránku nejsou k dispozici dostatečná CrUX data. Zobrazujeme data za celý web (origin).'}</span>
                </div>
              </div>
            )}

            {cruxData && lighthouseData && (
              <Suspense fallback={null}>
                <FieldVsLabBanner
                  cruxData={cruxData}
                  lighthouseData={lighthouseData}
                  selectedDevice={selectedDevice}
                />
              </Suspense>
            )}

            {viewMode === 'executive' ? (
              <Suspense fallback={<LazyFallback />}>
              <ExecutiveView
                metrics={dataSource === 'crux' ? metrics : currentMetrics}
                cruxData={cruxData}
                lighthouseData={lighthouseData}
                domain={cruxData?.usedTarget || domain}
                onShowDetail={(metric) => {
                  setViewMode('detailed');
                  if (metric) setActivePlaybook(metric);
                }}
                onShowAlerts={openAlertSettings}
                onShowMonitoringGuide={openMonitoringGuide}
                onShowComparison={() => {
                  enterCompareMode();
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                  setTimeout(() => {
                    const el = document.getElementById('comparison-selector');
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }, 300);
                }}
                hasAlertsConfigured={false}
                competitorCount={0}
                userStatus={userStatus}
                formFactorFractions={formFactorFractions}
                collectionPeriod={cruxData?.collectionPeriod}
                onUpgradeClick={() => setShowPricingModal(true)}
                 user={currentUser}
                 onOpenAuth={(mode) => { setAuthMode(mode); setShowAuthModal(true); }}
                 failingVitals={dataSource === 'crux' ? failingVitals : []}
                 diagnosticCandidates={dataSource === 'crux' ? diagnosticCandidates : null}
                 isLoading={loading}
              />
              </Suspense>
            ) : (
              <>
                <div id="section-vitals" data-section="core-vitals" className="mb-16 scroll-mt-24">
                  <Suspense fallback={<LazyFallback />}>
                  <CoreVitalsSection 
                    metrics={dataSource === 'crux' ? metrics : currentMetrics}
                    isLoading={loading}
                    onPlaybookClick={setActivePlaybook}
                    selectedDevice={selectedDevice}
                    onDeviceChange={handleFormFactorChange}
                    showDeviceToggle={true}
                    dataSource={dataSource}
                    deviceStatuses={coreVitalsDeviceStatuses}
                    url={cruxData?.usedTarget || domain}
                    user={currentUser}
                    onOpenAuth={(mode) => { setAuthMode(mode); setShowAuthModal(true); }}
                    onOpenPricing={() => setShowPricingModal(true)}
                    failingVitals={dataSource === 'crux' ? failingVitals : []}
                    byFormFactor={dataSource === 'crux' ? (cruxData?.byFormFactor || null) : null}
                    diagnosticCandidates={dataSource === 'crux' ? diagnosticCandidates : null}
                  />
                  </Suspense>
                </div>
            
            {activePlaybook && (
              <div id="playbook-panel" data-section="playbook" className="scroll-mt-36">
                <Suspense fallback={<LazyFallback />}>
                <PlaybookPanel 
                  metric={activePlaybook}
                  onClose={() => setActivePlaybook(null)}
                />
                </Suspense>
              </div>
            )}

            <div id="section-dalsi" className="mb-16 scroll-mt-24">
              <Suspense fallback={<LazyFallback />}>
              <OtherMetricsSection 
                metrics={dataSource === 'crux' ? metrics : currentMetrics}
                isLoading={loading}
                dataSource={dataSource}
                selectedDevice={selectedDevice}
                onDeviceChange={handleFormFactorChange}
                showDeviceToggle={true}
                deviceStatuses={otherMetricsDeviceStatuses}
                user={currentUser}
                onOpenAuth={(mode) => { setAuthMode(mode); setShowAuthModal(true); }}
                onOpenPricing={() => setShowPricing(true)}
                url={domain ? `https://${domain.replace(/^https?:\/\//, '')}` : null}
                strategy={selectedDevice === 'DESKTOP' ? 'desktop' : 'mobile'}
              />
              </Suspense>
            </div>

            {/* Extended Metrics (FP, DCL, ONLOAD) - CrUX only */}
            {dataSource === 'crux' && metrics && (
              <ExtendedMetricsDisplay metrics={metrics} />
            )}

            {/* Navigation Types - CrUX only */}
            {dataSource === 'crux' && (cruxData?.byFormFactor?.[selectedDevice]?.navigationTypes || cruxData?.byFormFactor?.ALL?.navigationTypes) && (
              <NavigationTypesDisplay navigationTypes={cruxData.byFormFactor[selectedDevice]?.navigationTypes || cruxData.byFormFactor.ALL.navigationTypes} />
            )}

            {/* LCP Resource Type - CrUX only */}
            {dataSource === 'crux' && (cruxData?.byFormFactor?.[selectedDevice]?.lcpResourceType || cruxData?.byFormFactor?.ALL?.lcpResourceType) && (
              <LcpResourceTypeDisplay lcpResourceType={cruxData.byFormFactor[selectedDevice]?.lcpResourceType || cruxData.byFormFactor.ALL.lcpResourceType} />
            )}

            {/* PageSpeed Metrics Summary Table - styled like CrUX table */}
            {dataSource === 'pagespeed' && currentMetrics && (
              <div>
                <PageSpeedMetricsSummaryTable metrics={currentMetrics} device={selectedDevice} />
              </div>
            )}

            {/* PageSpeed Extended Report */}
            {dataSource === 'pagespeed' && currentMetrics && (
              <PageSpeedExtendedReport 
                metrics={currentMetrics}
                device={selectedDevice === 'DESKTOP' ? 'desktop' : 'mobile'}
              />
            )}

            {/* PageSpeed Category Scores (Performance, Accessibility, SEO, Best Practices) */}
            {dataSource === 'pagespeed' && currentMetrics && (
              <CategoryScoresDisplay metrics={currentMetrics} />
            )}

            {/* PageSpeed Filmstrip - Visual loading progress */}
            {dataSource === 'pagespeed' && currentMetrics?.filmstrip && currentMetrics.filmstrip.length > 0 && (
              <FilmstripDisplay filmstrip={currentMetrics.filmstrip} />
            )}

            {/* PageSpeed Resource Summary */}
            {dataSource === 'pagespeed' && currentMetrics?.resourceSummary && currentMetrics.resourceSummary.length > 0 && (
              <ResourceSummaryDisplay 
                resourceSummary={currentMetrics.resourceSummary}
                networkRequests={currentMetrics.networkRequests}
              />
            )}

            {/* PageSpeed Main Thread Work */}
            {dataSource === 'pagespeed' && currentMetrics && (
              <MainThreadWorkDisplay 
                mainThreadWork={currentMetrics.mainThreadWork}
                bootupTime={currentMetrics.bootupTime}
                longTasks={currentMetrics.longTasks}
              />
            )}

            {/* PageSpeed Audit Results (Accessibility, SEO, Best Practices) */}
            {dataSource === 'pagespeed' && currentMetrics && (
              <AuditResultsSection 
                accessibilityAudits={currentMetrics.accessibilityAudits}
                seoAudits={currentMetrics.seoAudits}
                bestPracticesAudits={currentMetrics.bestPracticesAudits}
                allAccessibilityAudits={currentMetrics.allAccessibilityAudits}
                allSeoAudits={currentMetrics.allSeoAudits}
                allBestPracticesAudits={currentMetrics.allBestPracticesAudits}
              />
            )}

            {/* PageSpeed StackPacks - Framework-specific recommendations */}
            {dataSource === 'pagespeed' && currentMetrics?.stackPacks && currentMetrics.stackPacks.length > 0 && (
              <StackPacksDisplay stackPacks={currentMetrics.stackPacks} />
            )}

            {/* PageSpeed JS Libraries - Detected JavaScript libraries */}
            {dataSource === 'pagespeed' && currentMetrics?.jsLibraries && currentMetrics.jsLibraries.length > 0 && (
              <JsLibrariesDisplay jsLibraries={currentMetrics.jsLibraries} />
            )}

            {/* PageSpeed Additional Audits (HTTP/2, doctype, charset, etc.) */}
            {dataSource === 'pagespeed' && currentMetrics && (
              <AdditionalAuditsDisplay 
                usesHttp2={currentMetrics.usesHttp2}
                usesPassiveEventListeners={currentMetrics.usesPassiveEventListeners}
                noDocumentWrite={currentMetrics.noDocumentWrite}
                doctype={currentMetrics.doctype}
                charset={currentMetrics.charset}
                userTimings={currentMetrics.userTimings}
              />
            )}

            {/* PageSpeed Final Screenshot */}
            {dataSource === 'pagespeed' && currentMetrics && (currentMetrics.screenshot || currentMetrics.fullPageScreenshot) && (
              <FinalScreenshotDisplay 
                screenshot={currentMetrics.screenshot}
                fullPageScreenshot={currentMetrics.fullPageScreenshot}
              />
            )}

            {/* Device Metrics Summary Table - CrUX only */}
            {cruxData && dataSource === 'crux' && selectedDevice !== 'COMPARE' && (
              <div id="section-zarizeni" className="mt-16 scroll-mt-24">
                <DeviceMetricsSummaryTable 
                  cruxData={cruxData} 
                  url={domain ? `https://${domain.replace(/^https?:\/\//, '')}` : null}
                  strategy={selectedDevice === 'DESKTOP' ? 'desktop' : 'mobile'}
                  user={currentUser}
                  onOpenAuth={() => setShowAuthModal(true)}
                  onOpenPricing={() => setShowPricing(true)}
                />
              </div>
            )}

            {/* Histogram Distribution Table - CrUX only */}
            {cruxData && dataSource === 'crux' && selectedDevice !== 'COMPARE' && (
              <div>
                <HistogramDistributionTable cruxData={cruxData} formFactor={selectedDevice} onDeviceChange={setSelectedDevice} />
              </div>
            )}

            {/* Snapshot Evolution View - 28-day data (origin-only, hidden for URL queries) */}
            {cruxData && dataSource === 'crux' && diagnosticOrigin && (
              <Suspense fallback={<LazyFallback />}>
                <div>
                  <SnapshotEvolutionView 
                    origin={diagnosticOrigin}
                    history={diagnosticHistory}
                    trends={metricTrends}
                    onDiagnose={openTrendDiagnostic}
                    onDeviceChange={setSelectedDevice}
                  />
                </div>
              </Suspense>
            )}

            {/* CrUX History Chart - Weekly data (~40 weeks) */}
            {cruxData && dataSource === 'crux' && (
              <Suspense fallback={<LazyFallback />}>
                <div id="section-historie" data-section="history" className="scroll-mt-24">
                  <CrUXHistoryChart 
                    origin={cruxData?.usedTarget || (domain ? `https://${domain.replace(/^https?:\/\//, '')}` : null)}
                    queryMode={cruxData?.queryMode}
                    formFactor={selectedDevice !== 'COMPARE' ? selectedDevice : 'ALL'}
                    navigationTypes={cruxData?.byFormFactor?.ALL?.navigationTypes}
                    lcpResourceType={cruxData?.byFormFactor?.ALL?.lcpResourceType}
                    currentUser={currentUser}
                    onDeviceChange={setSelectedDevice}
                    selectedMetric={historySelectedMetric}
                    onMetricChange={setHistorySelectedMetric}
                    onExpandedChange={setHistoryExpanded}
                  />
                </div>
              </Suspense>
            )}

            {/* Cross-Device Weekly Comparison - right after CrUX History */}
            {cruxData && dataSource === 'crux' && (
              <Suspense fallback={<LazyFallback />}>
                <div>
                  <CrossDeviceWeeklyComparison 
                    origin={cruxData?.usedTarget || (domain ? `https://${domain.replace(/^https?:\/\//, '')}` : null)}
                    originDomain={(() => {
                      try {
                        const target = cruxData?.usedTarget || domain;
                        if (!target) return null;
                        return new URL(target.startsWith('http') ? target : `https://${target}`).origin;
                      } catch { return null; }
                    })()}
                    queryMode={cruxData?.queryMode}
                    onDeviceChange={setSelectedDevice}
                  />
                </div>
              </Suspense>
            )}

            {/* Monthly History Section - Monthly data from BigQuery (origin-only, hidden for URL queries) */}
            {cruxData && dataSource === 'crux' && cruxData.queryMode !== 'url' && (
              <ErrorBoundary>
                <Suspense fallback={<LazyFallback />}>
                  <div id="section-mesicni" className="scroll-mt-24">
                    <MonthlyHistorySection 
                      domain={cruxData?.usedTarget || (domain ? `https://${domain.replace(/^https?:\/\//, '')}` : null)}
                    />
                  </div>
                </Suspense>
              </ErrorBoundary>
            )}

            {/* Script Lifecycle Monitoring (origin-only, premium feature) */}
            {cruxData && cruxData.queryMode !== 'url' && AuthStorage.getUser() && (
              <ErrorBoundary>
                <Suspense fallback={<LazyFallback />}>
                  <div id="section-script-monitoring" className="scroll-mt-24">
                    <ScriptMonitoringSection
                      originUrl={cruxData?.usedTarget || (domain ? `https://${domain.replace(/^https?:\/\//, '')}` : null)}
                    />
                  </div>
                </Suspense>
              </ErrorBoundary>
            )}

            {/* BigQuery Extended Metrics - FP, DCL, Onload (origin-only, hidden for URL queries) */}
            {cruxData && dataSource === 'crux' && cruxData.queryMode !== 'url' && (
              <ErrorBoundary>
                <div id="section-server" className="scroll-mt-24">
                  <BigQueryMetricsSection 
                    origin={cruxData?.usedTarget || (domain ? `https://${domain.replace(/^https?:\/\//, '')}` : null)}
                    formFactor={selectedDevice !== 'COMPARE' ? selectedDevice : 'ALL'}
                    onDeviceChange={setSelectedDevice}
                    user={AuthStorage.getUser()}
                  />
                </div>
              </ErrorBoundary>
            )}

            {/* 4-Device Radar Grid - CrUX only */}
            {cruxData && dataSource === 'crux' && (
              <ErrorBoundary>
                <Suspense fallback={<LazyFallback />}>
                  <div className="mb-8">
                    <DeviceMetricsRadarGrid cruxData={cruxData} />
                  </div>
                </Suspense>
              </ErrorBoundary>
            )}
              </>
            )}

          </div>
        )}
        </>
        )}

      </main>

      <Footer 
        onPricingClick={() => setShowPricingModal(true)}
        apiStatus={apiStatus}
        onNavigateTo={navigateTo}
      />

      {showPricingModal && (
        <Suspense fallback={<LazyFallback />}>
          <PricingModal 
            onClose={() => setShowPricingModal(false)}
            currentUser={currentUser}
            onOpenAuth={(mode) => {
              setShowPricingModal(false);
              setAuthMode(mode || 'register');
              setShowAuthModal(true);
            }}
          />
        </Suspense>
      )}

      {showExportModal && (
        <Suspense fallback={<LazyFallback />}>
          <ExportModal 
            isOpen={showExportModal}
            onClose={() => setShowExportModal(false)}
            origin={domain ? `https://${domain.replace(/^https?:\/\//, '')}` : ''}
            formFactor={selectedDevice}
            cruxData={cruxData}
            historyData={historyData}
            selectedMetric={selectedMetric}
            navigationTypes={cruxData?.byFormFactor?.ALL?.navigationTypes}
            formFactorFractions={cruxData?.formFactorFractions}
            user={currentUser}
          />
        </Suspense>
      )}

      {consentShown && (
        <ConsentBanner 
          onAccept={handleConsent}
        />
      )}
      {showAnimationSettings && (
        <Suspense fallback={<LazyFallback />}>
          <AnimationSettingsModal 
            isOpen={showAnimationSettings}
            onClose={() => setShowAnimationSettings(false)}
          currentUser={currentUser}
          borderAnimationEnabled={borderAnimationEnabled}
          setBorderAnimationEnabled={setBorderAnimationEnabled}
          borderAnimSettings={borderAnimSettings}
          setBorderAnimSettings={setBorderAnimSettings}
          bgAnimationEnabled={bgAnimationEnabled}
          setBgAnimationEnabled={setBgAnimationEnabled}
          bgAnimSettings={bgAnimSettings}
          setBgAnimSettings={setBgAnimSettings}
          particleAnimationEnabled={particleAnimationEnabled}
          setParticleAnimationEnabled={setParticleAnimationEnabled}
          particleAnimSettings={particleAnimSettings}
          setParticleAnimSettings={setParticleAnimSettings}
          saveAnimationPreferences={saveAnimationPreferences}
          resetToDefaults={resetToDefaults}
          />
        </Suspense>
      )}

      {showAnalysisHistory && (
        <Suspense fallback={<LazyFallback />}>
          <AnalysisHistoryModal 
            isOpen={showAnalysisHistory}
            onClose={() => setShowAnalysisHistory(false)}
            user={currentUser}
            onLoadReport={(report) => {
              if (report?.origin) {
                const cleanDomain = report.origin.replace(/^https?:\/\//, '');
                handleAnalyze(cleanDomain, 'origin', { source: 'crux' });
              }
              setShowAnalysisHistory(false);
            }}
            onRerunReport={(data) => {
              if (data?.origin) {
                const cleanDomain = data.origin.replace(/^https?:\/\//, '');
                handleAnalyze(cleanDomain, 'origin', { source: 'crux' });
              }
              setShowAnalysisHistory(false);
            }}
          />
        </Suspense>
      )}

      {showMyAnalyses && (
        <Suspense fallback={<LazyFallback />}>
          <MyAnalysesModal 
            isOpen={showMyAnalyses}
            onClose={() => setShowMyAnalyses(false)}
            onNavigateTo={(page, url) => {
              if (page === 'health-check' && url) {
                navigateTo('health-check', url);
              }
            }}
            onLoadSavedAnalysis={async (analysisId) => {
              try {
                const data = await APIService.getSavedAnalysis(analysisId);
                if (data.analysis?.result_data) {
                  const rd = typeof data.analysis.result_data === 'string' ? JSON.parse(data.analysis.result_data) : data.analysis.result_data;
                  const savedDevice = rd.device || 'ALL';
                  const savedDataSource = rd.dataSource || 'crux';

                  if (rd.dataSource === 'diagnostics' && rd.diagnosticsData) {
                    const labResult = { ...rd.diagnosticsData, id: analysisId };
                    setLabData(labResult);
                    if (labResult.url) {
                      try { setDomain(new URL(labResult.url).origin); } catch {}
                    }
                    if (labResult.metrics) {
                      setCurrentMetrics({
                        LCP: { value: labResult.metrics.lcp, p75: labResult.metrics.lcp },
                        FCP: { value: labResult.metrics.fcp, p75: labResult.metrics.fcp },
                        CLS: { value: labResult.metrics.cls, p75: labResult.metrics.cls },
                        TTFB: { value: labResult.metrics.ttfb, p75: labResult.metrics.ttfb }
                      });
                    }
                    setSelectedDevice(savedDevice);
                    setDataSource(savedDataSource);
                  } else if (rd.cruxData) {
                    const crux = rd.cruxData;
                    setCruxData(crux);
                    setLighthouseData(rd.diagnosticsData || null);
                    if (data.analysis.url) {
                      setDomain(data.analysis.url);
                    }
                    setSelectedDevice(savedDevice);
                    setDataSource(savedDataSource);
                    if (crux.metrics) {
                      setCurrentMetrics(crux.metrics);
                    } else if (crux.byFormFactor?.[savedDevice]?.metrics) {
                      setCurrentMetrics(crux.byFormFactor[savedDevice].metrics);
                    }
                    setLabData(null);
                  }
                }
              } catch (e) {
                console.error('Failed to load saved analysis:', e);
              }
            }}
          />
        </Suspense>
      )}

      {showDomainGroupsModal && (
        <Suspense fallback={<LazyFallback />}>
          <DomainGroupsModal 
            isOpen={showDomainGroupsModal}
            onClose={() => setShowDomainGroupsModal(false)}
            currentUser={currentUser}
            onAnalyze={(origin) => {
              const cleanDomain = origin.replace(/^https?:\/\//, '');
              handleAnalyze(cleanDomain, 'origin', { source: 'crux' });
            }}
          />
        </Suspense>
      )}

      {showBadgesPanel && (
        <Suspense fallback={<LazyFallback />}>
          <div className="cv-app-portal fixed inset-0 z-[9999] flex items-center justify-center p-4">
            <div className="absolute inset-0 theme-overlay backdrop-blur-sm" onClick={closeBadges}></div>
            <div className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-slate-900/95 border border-slate-700 rounded-xl">
              <BadgesPanel origins={trackedOrigins} onClose={closeBadges} />
            </div>
          </div>
        </Suspense>
      )}

      {showAgencyPanel && (
        <Suspense fallback={<LazyFallback />}>
          <div className="cv-app-portal fixed inset-0 z-[9999] flex items-center justify-center p-4">
            <div className="absolute inset-0 theme-overlay backdrop-blur-sm" onClick={() => { closeAgency(); setAgencyInitialTab(null); }}></div>
            <div className="relative w-full max-w-5xl max-h-[90vh] overflow-y-auto bg-slate-900/95 border border-slate-700 rounded-xl">
              <AgencyPanel origins={trackedOrigins} onClose={() => { closeAgency(); setAgencyInitialTab(null); }} initialTab={agencyInitialTab} />
            </div>
          </div>
        </Suspense>
      )}

      {showIntegrationGuide && (
        <Suspense fallback={<LazyFallback />}>
          <IntegrationGuideModal
            isOpen={showIntegrationGuide}
            onClose={closeIntegrationGuide}
            onOpenAlertSettings={() => {
              closeIntegrationGuide();
              openAlertSettings();
            }}
          />
        </Suspense>
      )}

      {showMonitoringGuide && (
        <MonitoringGuide
          onClose={() => setShowMonitoringGuide(false)}
          onStartSetup={() => {
            setShowMonitoringGuide(false);
            openAlertSettings();
          }}
        />
      )}

      {showLabTest && (
        <div className="cv-app-portal fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowLabTest(false)}
          />
          <div 
            className="relative bg-slate-900/95 backdrop-blur-md rounded-xl border border-slate-700/50 w-full max-w-5xl max-h-[90vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-700 px-4 py-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                  <svg className="w-4 h-4 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                  </svg>
                </div>
                <h2 className="text-lg font-semibold text-white">Lab Test</h2>
              </div>
              <button
                onClick={() => setShowLabTest(false)}
                className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-700/50"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4">
              <LabTestPanel 
                origin={cruxData?.usedTarget || (domain ? `https://${domain}` : '')}
                user={currentUser}
                onShowPricing={() => {
                  setShowLabTest(false);
                  setShowPricingModal(true);
                }}
              />
            </div>
          </div>
        </div>
      )}

      {renderModals()}

      <FeedbackWidget user={currentUser} />

    </div>
  );
}

export default App;
